"use client"

// src/accordion/index.tsx
import * as RadixAccordion from "@radix-ui/react-accordion";
import { RiArrowDownSLine } from "@remixicon/react";
import { useState } from "react";
import { keyframes, styled } from "styled-components";

// src/theme/colors.ts
import { memo } from "@lb-lib/memo";

// src/theme/tokens.ts
var toDashCase = (str) => {
  return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`).replaceAll("_", "-");
};
var createThemeToken = (defaultValue) => (name) => {
  let id = toDashCase(name);
  let value = `var(--${id}${defaultValue ? `, ${defaultValue}` : ""})`;
  return Object.assign(value, {
    id,
    name,
    value,
    defaultValue,
    varName: `--${id}`
  });
};
var recursivelyCallThemeTokenFactory = (tokens) => {
  let result = {};
  for (let key in tokens) {
    let token = tokens[key];
    if (typeof token === "function") {
      result[key] = token(key);
    } else {
      result[key] = recursivelyCallThemeTokenFactory(token);
    }
  }
  return result;
};
var createTheme = (tokens) => {
  let values = recursivelyCallThemeTokenFactory(tokens);
  let setBodyStyles = (theme2) => {
    for (let value of Object.values(theme2)) {
      if ("value" in value) {
        if (value.defaultValue) {
          document.body.style.setProperty(value.varName, value.defaultValue);
        }
      } else if (typeof value === "object") {
        setBodyStyles(value);
      }
    }
  };
  return {
    ...values,
    setRootStyles: (theme2) => {
      let styles = {};
      let recurse = (theme3, values2) => {
        for (let key in theme3) {
          let value = theme3[key];
          if (typeof value == "string" || value.value) {
            let cssKey = values2[key]?.varName;
            if (value.value) value = value.value;
            if (value && cssKey) {
              styles[cssKey] = value;
            }
          } else {
            recurse(theme3[key], values2[key]);
          }
        }
      };
      recurse(theme2, values);
      return styles;
    },
    setBodyStyles: () => setBodyStyles(values)
  };
};

// src/theme/colors.ts
var colors = {
  gray100: "#fafafa",
  gray200: "#f6f6f6",
  gray300: "#efefef",
  gray400: "#ddd",
  gray500: "#ccc",
  gray600: "#999",
  gray700: "#555",
  gray800: "#333",
  gray900: "#222",
  blue100: "#fbfdff",
  blue200: "#f4faff",
  blue300: "#e6f4fe",
  blue400: "#d5efff",
  blue500: "#c2e5ff",
  blue600: "#acd8fc",
  blue700: "#8ec8f6",
  blue800: "#5eb1ef",
  blue900: "#0090ff",
  pink100: "#fffcfd",
  pink200: "#fef7f9",
  pink300: "#ffe9f0",
  pink400: "#fedce7",
  pink500: "#facedd",
  pink600: "#f3bed1",
  pink700: "#eaacc3",
  pink800: "#e093b2",
  pink900: "#e93d82",
  cyan100: "#fafdfe",
  cyan200: "#f2fafb",
  cyan300: "#def7f9",
  cyan400: "#caf1f6",
  cyan500: "#b5e9f0",
  cyan600: "#9ddde7",
  cyan700: "#7dcedc",
  cyan800: "#3db9cf",
  cyan900: "#00a2c7",
  indigo100: "#fdfdfe",
  indigo200: "#f7f9ff",
  indigo300: "#edf2fe",
  indigo400: "#e1e9ff",
  indigo500: "#d2deff",
  indigo600: "#c1d0ff",
  indigo700: "#abbdf9",
  indigo800: "#8da4ef",
  indigo900: "#3e63dd",
  iris100: "#fdfdff",
  iris200: "#f8f8ff",
  iris300: "#f0f1fe",
  iris400: "#e6e7ff",
  iris500: "#dadcff",
  iris600: "#cbcdff",
  iris700: "#b8baf8",
  iris800: "#9b9ef0",
  iris900: "#5b5bd6",
  green100: "#fbfefd",
  green200: "#f4fbf7",
  green300: "#e6f7ed",
  green400: "#d6f1e3",
  green500: "#c3e9d7",
  green600: "#acdec8",
  green700: "#8bceb6",
  green800: "#56ba9f",
  green900: "#29a383",
  orange100: "#fefcfb",
  orange200: "#fff7ed",
  orange300: "#ffefd6",
  orange400: "#ffdfb5",
  orange500: "#ffd19a",
  orange600: "#ffc182",
  orange700: "#f5ae73",
  orange800: "#ec9455",
  orange900: "#f76b15",
  purple100: "#fefcfe",
  purple200: "#fbf7fe",
  purple300: "#f7edfe",
  purple400: "#f2e2fc",
  purple500: "#ead5f9",
  purple600: "#e0c4f4",
  purple700: "#d1afec",
  purple800: "#be93e4",
  purple900: "#8e4ec6",
  red100: "#fffcfc",
  red200: "#fff7f7",
  red300: "#feebec",
  red400: "#ffdbdc",
  red500: "#ffcdce",
  red600: "#e5484d",
  red700: "#e5484d",
  red800: "#e5484d",
  red900: "#e5484d",
  violet100: "#fdfcfe",
  violet200: "#faf8ff",
  violet300: "#f4f0fe",
  violet400: "#ebe4ff",
  violet500: "#e1d9ff",
  violet600: "#d4cafe",
  violet700: "#c2b5f5",
  violet800: "#aa99ec",
  violet900: "#6e56cf",
  yellow100: "#fefdfb",
  yellow200: "#fefbe9",
  yellow300: "#fff7c2",
  yellow400: "#ffee9c",
  yellow500: "#fbe577",
  yellow600: "#f3d673",
  yellow700: "#e9c162",
  yellow800: "#e2a336",
  yellow900: "#ffc53d",
  white100: "#fff",
  white200: "#fff",
  white300: "#fff",
  white400: "#fff",
  white500: "#fff",
  white600: "#fff",
  white700: "#fff",
  white800: "#fff",
  white900: "#fff",
  black100: "#000",
  black200: "#000",
  black300: "#000",
  black400: "#000",
  black500: "#000",
  black600: "#000",
  black700: "#000",
  black800: "#000",
  black900: "#000",
  primary100: "#0099ff",
  primary200: "#0099ff",
  primary300: "#0099ff",
  primary400: "#0099ff",
  primary500: "#0099ff",
  primary600: "#0099ff",
  primary700: "#0099ff",
  primary800: "#0099ff",
  primary900: "#0099ff",
  primary: "#0099ff"
};
var colorThemeVars = Object.fromEntries(Object.entries(colors).map(([key, value]) => [key, createThemeToken(value)]));
var getColorKey = (color, facet) => `${color}${facet}`;
var getForegroundColor = memo((color) => {
  if (color.startsWith("white") || color.startsWith("yellow")) {
    return "#000";
  }
  let value = colors[color];
  let r = parseInt(value.substr(1, 2), 16);
  let g = parseInt(value.substr(3, 2), 16);
  let b = parseInt(value.substr(5, 2), 16);
  let brightness = (r * 299 + g * 587 + b * 114) / 1e3;
  return brightness > 125 ? "#000" : "#fff";
});

// src/theme/index.ts
var theme = createTheme({
  colors: {
    foreground: createThemeToken("#111111"),
    background: createThemeToken("#ffffff"),
    ...colorThemeVars
  },
  shadows: {
    small: createThemeToken("0 2px 4px rgba(0, 0, 0, 0.1)"),
    medium: createThemeToken("0 4px 8px rgba(0, 0, 0, 0.1)"),
    large: createThemeToken("0 8px 16px rgba(0, 0, 0, 0.1)")
  }
});

// src/accordion/index.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var slideDown = keyframes`
  from { height: 0 }
  to { height: var(--radix-accordion-content-height) }
`;
var slideUp = keyframes`
  from { height: var(--radix-accordion-content-height) }
  to { height: 0 }
`;
var Item2 = styled(RadixAccordion.Item).withConfig({
  componentId: "sc-10ipbiz-0"
})`border:solid 1px ${theme.colors.gray200};display:flex;flex-direction:column;transition:all 0.3s ease;&:focus-within{background:${theme.colors.gray100};}&:not(:last-child){border-bottom:solid 1px transparent;}&:first-child{border-top-left-radius:10px;border-top-right-radius:10px;}&:last-child{border-bottom-left-radius:10px;border-bottom-right-radius:10px;}&[data-state='open']{margin-bottom:20;border-radius:10px;box-shadow:${theme.shadows.medium};border:solid 1px ${theme.colors.gray800};background:transparent;}&:not(:first-child)[data-state='open']{margin-top:20;}&[data-before-open='true']{border-bottom:solid 1px ${theme.colors.gray200};border-bottom-left-radius:10px;border-bottom-right-radius:10px;}&[data-after-open='true']{border-top-left-radius:10px;border-top-right-radius:10px;}`;
var Trigger2 = styled(RadixAccordion.Trigger).withConfig({
  componentId: "sc-10ipbiz-1"
})`height:50px;display:flex;align-items:center;justify-content:space-between;padding:0 20px;font-size:16px;font-weight:600;border:none;outline:none;cursor:pointer;background:transparent;`;
var Content2 = styled(RadixAccordion.Content).withConfig({
  componentId: "sc-10ipbiz-2"
})`font-size:14px;border-top:solid 1px ${theme.colors.gray200};overflow:hidden;&[data-state='open']{animation:${slideDown} 0.3s ease forwards;}&[data-state='closed']{animation:${slideUp} 0.3s ease forwards;}`;
var Accordion = ({
  items,
  type = "single",
  disabled
}) => {
  let [value, setValue] = useState(() => type == "single" ? items.findIndex((item) => item.defaultOpen).toString() : items.filter((item) => item.defaultOpen).map((_, i) => i.toString()));
  return /* @__PURE__ */ jsx(RadixAccordion.Root, { type, disabled, value, onValueChange: setValue, children: items.map((item, i) => {
    let afterOpen = Array.isArray(value) ? value.includes((i - 1).toString()) : value == (i - 1).toString();
    let beforeOpen = Array.isArray(value) ? value.includes((i + 1).toString()) : value == (i + 1).toString();
    let isOpen = Array.isArray(value) ? value.includes(i.toString()) : value == i.toString();
    return /* @__PURE__ */ jsxs(Item2, { value: i.toString(), "data-before-open": beforeOpen && !isOpen, "data-after-open": afterOpen && !isOpen, children: [
      /* @__PURE__ */ jsxs(Trigger2, { children: [
        /* @__PURE__ */ jsx("span", { children: item.title }),
        /* @__PURE__ */ jsx(RiArrowDownSLine, { style: {
          transform: `rotate(${isOpen ? 180 : 0}deg)`,
          transition: "transform 200ms ease",
          opacity: 0.5
        }, size: 16 })
      ] }),
      /* @__PURE__ */ jsx(Content2, { children: /* @__PURE__ */ jsx("div", { style: {
        padding: 20
      }, children: item.content }) })
    ] }, i);
  }) });
};
var AccordionSingle = ({
  title,
  children,
  disabled,
  defaultOpen
}) => {
  return /* @__PURE__ */ jsx(Accordion, { items: [{
    title,
    content: children,
    defaultOpen
  }], disabled, type: "multiple" });
};

// src/alert/index.tsx
import * as RadixAlertDialog from "@radix-ui/react-alert-dialog";
import { keyframes as keyframes2, styled as styled2 } from "styled-components";

// src/dialog/state.ts
import React2, { useContext, useEffect, useState as useState2 } from "react";
var useDialog = (initialState = false) => {
  let [isOpen, setIsOpen] = useState2(initialState);
  return {
    state: {
      isOpen,
      onOpenChange: setIsOpen
    },
    isOpen,
    open: () => setIsOpen(true)
  };
};
var currentZIndexRef = {
  value: 1e3
};
var useDialogZIndex = (isOpen) => {
  let [zIndex, setZIndex] = useState2(() => currentZIndexRef.value + 5);
  useEffect(() => {
    currentZIndexRef.value += 10;
    setZIndex(currentZIndexRef.value);
  }, [isOpen]);
  return zIndex;
};
var DialogContext = React2.createContext(null);
var DialogProvider = DialogContext.Provider;
var useDialogContext = () => useContext(DialogContext);
var useIsInDialog = () => !!useContext(DialogContext);

// src/alert/index.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var fadeInShift = keyframes2`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;
var fadeOutShift = keyframes2`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
`;
var fadeIn = keyframes2`
  from {
    opacity: 0;
  }
  to {
    opacity: 0.4;
  }
`;
var fadeOut = keyframes2`
  from {
    opacity: 0.4;
  }
  to {
    opacity: 0;
  }
`;
var Overlay2 = styled2(RadixAlertDialog.Overlay).withConfig({
  componentId: "sc-1524gu-0"
})`position:fixed;top:0;left:0;right:0;bottom:0;background:${theme.colors.gray600};z-index:1000;&[data-state='open']{animation:${fadeIn} 200ms ease-out forwards;}&[data-state='closed']{animation:${fadeOut} 200ms ease-in forwards;}`;
var Content4 = styled2(RadixAlertDialog.Content).withConfig({
  componentId: "sc-1524gu-1"
})`position:fixed;z-index:1001;top:50%;left:50%;transform:translate(-50%,-50%);width:90vw;max-width:400px;max-height:90vh;overflow:auto;background:${theme.colors.gray200};border-radius:10px;box-shadow:0 0 10px rgba(0,0,0,0.1);color:${theme.colors.gray900};&[data-state='open']{animation:${fadeInShift} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutShift} 200ms ease-in;}`;
var Title2 = styled2(RadixAlertDialog.Title).withConfig({
  componentId: "sc-1524gu-2"
})`padding:20px 20px 0px 20px;font-size:20px;font-weight:600;`;
var Description2 = styled2(RadixAlertDialog.Description).withConfig({
  componentId: "sc-1524gu-3"
})`padding:6px 20px 0px 20px;font-size:14px;color:${theme.colors.gray600};`;
var Actions = styled2("div").withConfig({
  componentId: "sc-1524gu-4"
})`display:flex;justify-content:flex-end;gap:10px;padding:20px;`;
var Alert = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    return /* @__PURE__ */ jsx2(RadixAlertDialog.Root, { open: isOpen, onOpenChange, children: /* @__PURE__ */ jsxs2(RadixAlertDialog.Portal, { children: [
      /* @__PURE__ */ jsx2(Overlay2, { style: {
        zIndex
      } }),
      /* @__PURE__ */ jsx2(Content4, { style: {
        zIndex: zIndex + 1
      }, children })
    ] }) });
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ jsx2(Title2, { children });
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ jsx2(Description2, { children });
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ jsx2(Actions, { children });
  },
  Action: ({
    children,
    type
  }) => {
    if (type == "cancel") {
      return /* @__PURE__ */ jsx2(RadixAlertDialog.Cancel, { asChild: true, children });
    }
    return /* @__PURE__ */ jsx2(RadixAlertDialog.Action, { asChild: true, children });
  }
};

// src/animateContent/index.tsx
import { motion } from "framer-motion";
import { useEffect as useEffect2, useMemo, useRef, useState as useState3 } from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var AnimateContent = (props) => {
  let [prevIdentifier, setPrevIdentifier] = useState3(() => props.identifier);
  let direction = useMemo(() => {
    if (props.identifier != prevIdentifier) return "animate";
    return "none";
  }, [props.identifier, prevIdentifier]);
  let prevChildren = useRef(props.children);
  useEffect2(() => {
    let to = setTimeout(() => {
      setPrevIdentifier(props.identifier);
      prevChildren.current = props.children;
    }, 300);
    return () => clearTimeout(to);
  }, [props.identifier]);
  useEffect2(() => {
    if (direction == "none") prevChildren.current = props.children;
  }, [props.children, direction]);
  return /* @__PURE__ */ jsxs3("div", { style: {
    // display: 'flex',
    width: "100%",
    position: "relative",
    padding: `0px ${props.paddingInline ?? 20}px`
  }, children: [
    /* @__PURE__ */ jsx3(motion.div, { initial: direction == "animate" ? {
      opacity: 0,
      scale: 1.2
    } : {
      opacity: 1
    }, animate: {
      opacity: 1,
      scale: 1
    }, transition: {
      duration: 0.2,
      ease: "easeInOut"
    }, children: props.children }, props.identifier),
    direction != "none" && /* @__PURE__ */ jsx3(motion.div, { initial: {
      opacity: 1,
      scale: 1
    }, animate: {
      opacity: 0,
      scale: 0.8
    }, transition: {
      duration: 0.2,
      ease: "easeInOut"
    }, style: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%"
    }, children: prevChildren.current }, prevIdentifier)
  ] });
};

// src/animateHeight/index.tsx
import { motion as motion2 } from "framer-motion";
import { useMeasure } from "react-use";
import { jsx as jsx4 } from "react/jsx-runtime";
var AnimateHeight = (props) => {
  let [ref, {
    height
  }] = useMeasure();
  return /* @__PURE__ */ jsx4(motion2.div, { animate: {
    height
  }, transition: {
    duration: 0.2
  }, style: {
    width: "100%",
    overflowY: "hidden"
  }, children: /* @__PURE__ */ jsx4("div", { ref, children: props.children }) });
};

// src/animatePanes/index.tsx
import { motion as motion3 } from "framer-motion";
import { useEffect as useEffect3, useMemo as useMemo2, useRef as useRef2, useState as useState4 } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var AnimatePanes = (props) => {
  let [prevOrderedIdentifier, setPrevOrderedIdentifier] = useState4(() => props.orderedIdentifier);
  let direction = useMemo2(() => {
    if (props.orderedIdentifier > prevOrderedIdentifier) return "left";
    if (props.orderedIdentifier < prevOrderedIdentifier) return "right";
    return "none";
  }, [props.orderedIdentifier, prevOrderedIdentifier]);
  let prevChildren = useRef2(props.children);
  useEffect3(() => {
    let to = setTimeout(() => {
      setPrevOrderedIdentifier(props.orderedIdentifier);
      prevChildren.current = props.children;
    }, 300);
    return () => clearTimeout(to);
  }, [props.orderedIdentifier]);
  useEffect3(() => {
    if (direction == "none") prevChildren.current = props.children;
  }, [props.children, direction]);
  return /* @__PURE__ */ jsx5(AnimateHeight, { children: /* @__PURE__ */ jsxs4("div", { style: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "hidden",
    position: "relative",
    padding: 3
  }, children: [
    /* @__PURE__ */ jsx5(motion3.div, { initial: {
      x: direction == "right" ? "-100%" : direction == "left" ? "100%" : 0
    }, animate: {
      x: 0
    }, transition: {
      duration: 0.2,
      ease: "easeInOut"
    }, children: props.children }, props.orderedIdentifier),
    direction != "none" && /* @__PURE__ */ jsx5(motion3.div, { initial: {
      x: 0
    }, animate: {
      x: direction == "right" ? "100%" : "-100%"
    }, transition: {
      duration: 0.2,
      ease: "easeInOut"
    }, style: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%"
    }, children: prevChildren.current }, prevOrderedIdentifier)
  ] }) });
};

// src/atoms/index.ts
import { useLayoutEffect, useState as useState5 } from "react";

// src/atoms/listeners.ts
var createListeners = () => {
  let listeners = /* @__PURE__ */ new Set();
  let register = (listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  let notify = () => {
    for (let listener of listeners) listener();
  };
  let dispose = () => {
    listeners.clear();
  };
  return {
    register,
    notify,
    dispose
  };
};

// src/atoms/index.ts
var atom = (initialValue) => {
  let value = initialValue;
  let listeners = createListeners();
  let set = (newValue) => {
    if (typeof newValue === "function") {
      value = newValue(value);
    } else {
      value = newValue;
    }
    listeners.notify();
  };
  let get = () => value;
  let subscribe = (listener) => {
    let cb = () => listener(get());
    return listeners.register(cb);
  };
  return {
    set,
    get,
    subscribe
  };
};
var useAtom = (a) => {
  let [_, setState] = useState5({});
  useLayoutEffect(() => a.subscribe(() => setState({})), [a]);
  return a.get();
};

// src/attributes/index.tsx
import { styled as styled4 } from "styled-components";

// src/text/code.tsx
import { styled as styled3 } from "styled-components";

// src/text/constants.ts
var textSizeSteps = {
  "1": {
    fontSize: "12px",
    letterSpacing: "0.0025em",
    lineHeight: "16px"
  },
  "2": {
    fontSize: "14px",
    letterSpacing: "0em",
    lineHeight: "20px"
  },
  "3": {
    fontSize: "16px",
    letterSpacing: "0em",
    lineHeight: "24px"
  },
  "4": {
    fontSize: "18px",
    letterSpacing: "-0.0025em",
    lineHeight: "26px"
  },
  "5": {
    fontSize: "20px",
    letterSpacing: "-0.005em",
    lineHeight: "28px"
  },
  "6": {
    fontSize: "24px",
    letterSpacing: "-0.00625em",
    lineHeight: "30px"
  },
  "7": {
    fontSize: "28px",
    letterSpacing: "-0.0075em",
    lineHeight: "36px"
  },
  "8": {
    fontSize: "35px",
    letterSpacing: "-0.01em",
    lineHeight: "40px"
  },
  "9": {
    fontSize: "60px",
    letterSpacing: "-0.025em",
    lineHeight: "60px"
  }
};
var textWeights = {
  light: {
    fontWeight: 300
  },
  regular: {
    fontWeight: 400
  },
  medium: {
    fontWeight: 500
  },
  strong: {
    fontWeight: 600
  },
  bold: {
    fontWeight: 700
  }
};
var textAlignments = {
  left: {
    textAlign: "left"
  },
  center: {
    textAlign: "center"
  },
  right: {
    textAlign: "right"
  }
};
var textWrap = {
  wrap: {
    whiteSpace: "normal"
  },
  nowrap: {
    whiteSpace: "nowrap"
  },
  balance: {
    textWrap: "balance"
  }
};
var textTransforms = {
  uppercase: {
    textTransform: "uppercase"
  },
  lowercase: {
    textTransform: "lowercase"
  },
  capitalize: {
    textTransform: "capitalize"
  }
};
var truncateStyles = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};
var getTextStyles = (props) => ({
  margin: 0,
  padding: 0,
  fontFamily: "inherit",
  ...textSizeSteps[props.size || "2"],
  ...textWeights[props.weight || "regular"],
  ...textAlignments[props.align || "left"],
  ...textWrap[props.wrap || "wrap"],
  ...props.transform && textTransforms[props.transform],
  ...props.truncate && truncateStyles,
  color: props.color ? theme.colors[props.color] : theme.colors.foreground
});

// src/text/code.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var StyledCode = styled3("code").withConfig({
  componentId: "sc-1ppnjx2-0"
})`font-family:'JetBrains Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,Courier,monospace;background:transparent;padding:0.2em 0.4em;color:${theme.colors.gray900};`;
var Code = (props) => {
  let size = getTextStyles(props);
  return /* @__PURE__ */ jsx6(StyledCode, { style: {
    ...size,
    ...props.style
  }, children: props.children });
};
var StyledKbd = styled3("kbd").withConfig({
  componentId: "sc-1ppnjx2-1"
})`background-color:${theme.colors.gray100};box-shadow:inset 0 -1px 0 ${theme.colors.gray300};border-radius:0.2em;padding:0.1em 0.3em;font-size:0.9em;font-family:'JetBrains Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,Courier,monospace;`;
var Kbd = (props) => {
  return /* @__PURE__ */ jsx6(StyledKbd, { style: {
    ...props.style
  }, children: props.children });
};

// src/text/text.tsx
import React3 from "react";
import Balancer from "react-wrap-balancer";
import { jsx as jsx7 } from "react/jsx-runtime";
var Text = (props) => {
  let styles = getTextStyles(props);
  return React3.createElement(props.as ?? "p", {
    style: {
      ...styles,
      ...props.style
    }
  }, props.balance ? /* @__PURE__ */ jsx7(Balancer, { style: {
    textAlign: props.align
  }, children: props.children }) : props.children);
};
var Title3 = (props) => {
  let styles = getTextStyles(props);
  return React3.createElement(props.as ?? "h1", {
    style: {
      ...styles,
      ...props.style
    }
  }, props.balance ? /* @__PURE__ */ jsx7(Balancer, { style: {
    textAlign: props.align
  }, children: props.children }) : props.children);
};

// src/attributes/index.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
var Wrapper = styled4("div").withConfig({
  componentId: "sc-1fjm0co-0"
})`display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:1px;background:${theme.colors.gray300};border:1px solid ${theme.colors.gray300};border-radius:10px;overflow:hidden;`;
var Attribute = styled4("div").withConfig({
  componentId: "sc-1fjm0co-1"
})`background:${theme.colors.background};padding:15px;display:flex;align-items:center;`;
var Inner = styled4("div").withConfig({
  componentId: "sc-1fjm0co-2"
})`display:flex;flex-direction:column;gap:3px;`;
var Attributes = ({
  attributes
}) => {
  return /* @__PURE__ */ jsx8(Wrapper, { children: attributes.map((attr, i) => /* @__PURE__ */ jsx8(Attribute, { children: /* @__PURE__ */ jsxs5(Inner, { children: [
    /* @__PURE__ */ jsx8(Text, { weight: "bold", size: "1", children: attr.label }),
    /* @__PURE__ */ jsx8(Text, { size: "2", weight: "medium", color: "gray700", children: attr.content })
  ] }) }, i)) });
};

// src/avatar/index.tsx
import { useRef as useRef3, useState as useState7 } from "react";
import { useInterval } from "react-use";

// src/tooltip/index.tsx
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { useEffect as useEffect4, useState as useState6 } from "react";
import Balancer2 from "react-wrap-balancer";
import { keyframes as keyframes3, styled as styled5 } from "styled-components";
import { Fragment, jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
var fadeInTop = keyframes3`
  from { opacity: 0; transform: translateY(-15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
`;
var fadeOutTop = keyframes3`
  from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateY(-15px) scale(0.75); filter: blur(2px) }
`;
var fadeInBottom = keyframes3`
  from { opacity: 0; transform: translateY(15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
`;
var fadeOutBottom = keyframes3`
  from { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateY(15px) scale(0.75); filter: blur(2px) }
`;
var fadeInLeft = keyframes3`
  from { opacity: 0; transform: translateX(15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
`;
var fadeOutLeft = keyframes3`
  from { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateX(15px) scale(0.75); filter: blur(2px) }
`;
var fadeInRight = keyframes3`
  from { opacity: 0; transform: translateX(-15px) scale(0.75); filter: blur(2px) }
  to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
`;
var fadeOutRight = keyframes3`
  from { opacity: 1; transform: translateX(0) scale(1); filter: blur(0px) }
  to { opacity: 0; transform: translateX(-15px) scale(0.75); filter: blur(2px) }
`;
var Content6 = styled5(RadixTooltip.Content).withConfig({
  componentId: "sc-l2a16c-0"
})`display:flex;gap:10px;align-items:center;cursor:default;background:${theme.colors.foreground};color:${theme.colors.background};border-radius:7px;box-shadow:${theme.shadows.medium};will-change:transform,opacity;padding:8px 10px;font-size:12px;font-weight:500;max-width:400px;z-index:99999;&[data-state='instant-open'][data-side='top']{animation:${fadeInBottom} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='top']{animation:${fadeInBottom} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutBottom} 0.2s ease forwards;}&[data-state='instant-open'][data-side='bottom']{animation:${fadeInTop} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='bottom']{animation:${fadeInTop} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutTop} 0.2s ease forwards;}&[data-state='instant-open'][data-side='left']{animation:${fadeInLeft} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='left']{animation:${fadeInLeft} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft} 0.2s ease forwards;}&[data-state='instant-open'][data-side='right']{animation:${fadeInRight} 0.2s ease forwards;}&[data-state='delayed-open'][data-side='right']{animation:${fadeInRight} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight} 0.2s ease forwards;}`;
var Arrow2 = styled5(RadixTooltip.Arrow).withConfig({
  componentId: "sc-l2a16c-1"
})`fill:${theme.colors.foreground};`;
var Tooltip = ({
  content,
  children,
  arrow,
  delayDuration,
  enabled,
  side = "bottom",
  align = "center"
}) => {
  let [disabled, setDisabled] = useState6(true);
  useEffect4(() => {
    setDisabled(false);
  }, []);
  if (enabled === false) return /* @__PURE__ */ jsx9(Fragment, { children });
  return /* @__PURE__ */ jsx9(RadixTooltip.Provider, { delayDuration, children: /* @__PURE__ */ jsxs6(RadixTooltip.Root, { children: [
    /* @__PURE__ */ jsx9(RadixTooltip.Trigger, { asChild: true, disabled, children }),
    /* @__PURE__ */ jsx9(RadixTooltip.Portal, { children: /* @__PURE__ */ jsxs6(Content6, { sideOffset: 5, side, align, hideWhenDetached: true, children: [
      typeof content == "string" ? /* @__PURE__ */ jsx9(Fragment, { children: /* @__PURE__ */ jsx9(Balancer2, { children: content }) }) : /* @__PURE__ */ jsx9(Fragment, { children: content }),
      arrow && /* @__PURE__ */ jsx9(Arrow2, {})
    ] }) })
  ] }) });
};

// src/avatar/index.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var loadedSet = /* @__PURE__ */ new Set();
var Avatar = ({
  entity,
  size = 30,
  noTooltip
}) => {
  let src = entity?.photoUrl || entity?.imageUrl;
  let name = entity?.name;
  let ref = useRef3(null);
  let [loaded, setLoaded] = useState7(() => loadedSet.has(src));
  useInterval(() => {
    if (loaded || !ref.current) return;
    if (ref.current.complete) {
      setLoaded(true);
      loadedSet.add(src);
    }
  }, 100);
  let inner = /* @__PURE__ */ jsx10("div", { style: {
    width: size,
    aspectRatio: "1 / 1",
    borderRadius: "50%",
    backgroundColor: theme.colors.gray200,
    display: "flex"
  }, children: src && /* @__PURE__ */ jsx10("img", { src, alt: name, ref, style: {
    width: size,
    aspectRatio: "1 / 1",
    borderRadius: "50%",
    objectFit: "cover",
    opacity: loaded ? 1 : 0,
    transition: "opacity 0.2s"
  }, onLoad: () => {
    setLoaded(true);
    loadedSet.add(src);
  } }) });
  if (noTooltip) return inner;
  return /* @__PURE__ */ jsx10(Tooltip, { content: name, children: inner });
};

// src/badge/constants.ts
import { memo as memo2 } from "@lb-lib/memo";
var badgeColor = memo2((color) => ({
  color: theme.colors[getColorKey(color, "900")],
  backgroundColor: theme.colors[getColorKey(color, "300")]
}));
var sizes = {
  "1": {
    padding: "2px 6px",
    fontSize: 10,
    borderRadius: 4
  },
  "2": {
    padding: "4px 10px",
    fontSize: 14,
    borderRadius: 6
  },
  "3": {
    padding: "8px 16px",
    fontSize: 20,
    borderRadius: 8
  }
};
var getBadgeStyles = ({
  color,
  size = "2"
}) => ({
  ...badgeColor(color),
  ...sizes[size]
});

// src/badge/index.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var Badge = ({
  children,
  color,
  size,
  style
}) => {
  let styles = getBadgeStyles({
    color,
    size
  });
  return /* @__PURE__ */ jsx11("span", { style: {
    ...styles,
    ...style,
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600
  }, children });
};

// src/button/index.tsx
import { AnimatePresence, motion as motion4 } from "framer-motion";
import React5, { useEffect as useEffect5, useRef as useRef4, useState as useState8 } from "react";
import { Check } from "react-feather";
import { styled as styled8 } from "styled-components";
import _useDelayed from "use-delayed";

// src/spinner/index.tsx
import { keyframes as keyframes4, styled as styled6 } from "styled-components";
import { jsx as jsx12 } from "react/jsx-runtime";
var spinnerTheme = createTheme({
  duration: createThemeToken(),
  size: createThemeToken(),
  foreground: createThemeToken(),
  background: createThemeToken()
});
var rotate = keyframes4`
  0% { transform: rotate(0deg) }
  100% { transform: rotate(360deg) }
`;
var Wrapper2 = styled6("div").withConfig({
  componentId: "sc-1ut9w0t-0"
})`display:inline-block;animation:${rotate} ${spinnerTheme.duration} ease infinite;border:calc(${spinnerTheme.size} / 6) solid ${spinnerTheme.background};border-top-color:${spinnerTheme.foreground};border-radius:50%;width:${spinnerTheme.size};height:${spinnerTheme.size};`;
var Spinner = ({
  size,
  foreground,
  background,
  duration,
  center
}) => {
  if (typeof size == "number") size = `${size}px`;
  return /* @__PURE__ */ jsx12(Wrapper2, { "aria-live": "polite", title: "Loading...", style: {
    ...spinnerTheme.setRootStyles({
      duration: duration ?? "900ms",
      size: (size ?? "26px").toString(),
      foreground: foreground ?? theme.colors.blue700,
      background: background ?? theme.colors.gray300
    }),
    margin: center ? "0px auto" : void 0
  } });
};
var CenteredSpinner = (d) => /* @__PURE__ */ jsx12("div", { style: {
  display: "flex",
  justifyContent: "center"
}, children: /* @__PURE__ */ jsx12(Spinner, { ...d, center: true }) });

// src/button/constants.ts
import { memo as memo3 } from "@lb-lib/memo";

// src/theme/gradient.ts
var linearGradient = (angle, ...colors2) => {
  return `linear-gradient(${angle}deg, ${colors2.join(", ")})`;
};

// src/button/constants.ts
var coloredButtonVariants = memo3((color) => ({
  solid: {
    passive: {
      color: getForegroundColor(getColorKey(color, "900")),
      background: theme.colors[getColorKey(color, "700")],
      border: theme.colors[getColorKey(color, "700")],
      shadow: theme.shadows.small
    },
    active: {
      color: getForegroundColor(getColorKey(color, "900")),
      background: theme.colors[getColorKey(color, "700")],
      border: theme.colors[getColorKey(color, "700")],
      shadow: theme.shadows.medium
    },
    spinner: {
      foreground: theme.colors.background,
      background: theme.colors[getColorKey(color, "400")]
    },
    animateClickScale: true
  },
  outline: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: theme.colors[getColorKey(color, "600")],
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: theme.colors[getColorKey(color, "700")],
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    },
    animateClickScale: false
  },
  soft: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors[getColorKey(color, "100")],
      border: theme.colors[getColorKey(color, "100")],
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "800")],
      background: theme.colors[getColorKey(color, "200")],
      border: theme.colors[getColorKey(color, "200")],
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    },
    animateClickScale: false
  },
  ghost: {
    passive: {
      color: theme.colors[getColorKey(color, "800")],
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    active: {
      color: theme.colors[getColorKey(color, "900")],
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors[getColorKey(color, "900")],
      background: theme.colors[getColorKey(color, "200")]
    },
    animateClickScale: false
  }
}));
var monoChromeButtonVariants = {
  solid: {
    passive: {
      color: theme.colors.background,
      background: linearGradient(0, theme.colors.gray800, theme.colors.gray900),
      border: "transparent",
      shadow: theme.shadows.small
    },
    active: {
      color: theme.colors.background,
      background: linearGradient(0, theme.colors.gray800, theme.colors.gray900),
      border: "transparent",
      shadow: theme.shadows.medium
    },
    spinner: {
      foreground: theme.colors.background,
      background: theme.colors.gray700
    },
    animateClickScale: true
  },
  outline: {
    passive: {
      color: theme.colors.foreground,
      background: "transparent",
      border: theme.colors.gray300,
      shadow: "none"
    },
    active: {
      color: theme.colors.foreground,
      background: "transparent",
      border: theme.colors.gray400,
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray100
    },
    animateClickScale: false
  },
  soft: {
    passive: {
      color: theme.colors.foreground,
      background: theme.colors.gray200,
      border: theme.colors.gray100,
      shadow: "none"
    },
    active: {
      color: theme.colors.foreground,
      background: theme.colors.gray300,
      border: theme.colors.gray200,
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray200
    },
    animateClickScale: false
  },
  ghost: {
    passive: {
      color: theme.colors.gray600,
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    active: {
      color: theme.colors.gray700,
      background: "transparent",
      border: "transparent",
      shadow: "none"
    },
    spinner: {
      foreground: theme.colors.foreground,
      background: theme.colors.gray200
    },
    animateClickScale: false
  }
};
var sizes2 = {
  "1": {
    gap: "6px",
    height: "26px",
    padding: "0 8px",
    borderRadius: "6px",
    fontSize: "12px"
  },
  "2": {
    gap: "8px",
    height: "30px",
    padding: "0 10px",
    borderRadius: "8px",
    fontSize: "14px"
  },
  "3": {
    gap: "10px",
    height: "36px",
    padding: "0 12px",
    borderRadius: "10px",
    fontSize: "16px"
  },
  "4": {
    gap: "12px",
    height: "42px",
    padding: "0 14px",
    borderRadius: "12px",
    fontSize: "18px"
  },
  "5": {
    gap: "14px",
    height: "48px",
    padding: "0 16px",
    borderRadius: "14px",
    fontSize: "20px"
  }
};
var buttonTheme = createTheme({
  passive_color: createThemeToken(),
  passive_background: createThemeToken(),
  passive_border: createThemeToken(),
  passive_shadow: createThemeToken(),
  active_color: createThemeToken(),
  active_background: createThemeToken(),
  active_border: createThemeToken(),
  active_shadow: createThemeToken(),
  active_transform: createThemeToken(),
  spinner_foreground: createThemeToken(),
  spinner_background: createThemeToken()
});
var getColorButtonTheme = memo3((color, variant) => {
  let v = (color ? coloredButtonVariants(color) : monoChromeButtonVariants)[variant];
  return buttonTheme.setRootStyles({
    passive_color: v.passive.color,
    passive_background: v.passive.background,
    passive_border: v.passive.border,
    passive_shadow: v.passive.shadow,
    active_color: v.active.color,
    active_background: v.active.background,
    active_border: v.active.border,
    active_shadow: v.active.shadow,
    active_transform: v.animateClickScale ? "scale(0.98)" : "none",
    spinner_foreground: v.spinner.foreground,
    spinner_background: v.spinner.background
  });
});
var getButtonStyles = (props) => {
  let size = sizes2[props.size ?? "3"];
  if (props.variant == "ghost") {
    size = {
      ...size,
      padding: "0",
      height: "auto"
    };
  }
  return {
    margin: 0,
    fontFamily: "inherit",
    ...size,
    ...getColorButtonTheme(props.color, props.variant ?? "solid")
  };
};
var getButtonSize = (size) => sizes2[size];

// src/button/linkButton.ts
import { styled as styled7 } from "styled-components";
var LinkButton = styled7("button").withConfig({
  componentId: "sc-vm11n0-0"
})`background:none;border:none;cursor:pointer;font-size:inherit;font-weight:inherit;color:inherit;padding:0;text-decoration:underline;transition:color 0.2s;line-height:inherit;`;

// src/button/index.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var useDelayed = _useDelayed;
if (useDelayed.default) useDelayed = useDelayed.default;
var Wrapper3 = styled8("button").withConfig({
  componentId: "sc-1dy86e6-0"
})`display:inline-flex;align-items:center;flex-shrink:0;position:relative;cursor:pointer;color:${buttonTheme.passive_color};background:${buttonTheme.passive_background};border:solid 1px ${buttonTheme.passive_border};box-shadow:${buttonTheme.passive_shadow};transition:all 0.2s;user-select:none;&:hover,&:focus{color:${buttonTheme.active_color};background:${buttonTheme.active_background};border:solid 1px ${buttonTheme.active_border};box-shadow:${buttonTheme.active_shadow};}&:active{transform:${buttonTheme.active_transform};}&:disabled{cursor:not-allowed;opacity:0.7;}`;
var Overlay3 = styled8(motion4.div).withConfig({
  componentId: "sc-1dy86e6-1"
})`position:absolute;display:flex;align-items:center;justify-content:center;top:0;left:0;right:0;bottom:0;`;
var OverlayInner = styled8(motion4.div).withConfig({
  componentId: "sc-1dy86e6-2"
})`display:flex;align-items:center;justify-content:center;`;
var Inner2 = styled8("div").withConfig({
  componentId: "sc-1dy86e6-3"
})`display:inline-flex;align-items:center;justify-content:center;flex-grow:1;flex-shrink:0;gap:6px;`;
var Icon = styled8("span").withConfig({
  componentId: "sc-1dy86e6-4"
})`display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;svg{height:16px;width:16px;}`;
var Content7 = styled8("span").withConfig({
  componentId: "sc-1dy86e6-5"
})`display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.5;font-weight:500;`;
var Button = React5.forwardRef(({
  children,
  iconLeft,
  iconRight,
  loading,
  success,
  fullWidth,
  variant,
  color,
  size,
  onHover,
  as = "button",
  ...props
}, ref) => {
  let [isHovered, setIsHovered] = useState8(false);
  useEffect5(() => onHover?.(), [isHovered]);
  let isMouseOverRef = useRef4(false);
  let style = getButtonStyles({
    color,
    variant,
    size
  });
  let loadingVisible = useDelayed(loading, 200, [false]);
  let successVisible = useDelayed(success, 200, [false]);
  let contentVisible = useDelayed(!loading && !success, 200);
  return /* @__PURE__ */ jsxs7(Wrapper3, { as, ref, ...props, style: {
    ...style,
    ...props.style,
    width: fullWidth ? "100%" : void 0,
    ...!children && {
      width: style.height,
      padding: 0
    }
  }, disabled: props.disabled, onClick: (e) => {
    if (loading || success) e.preventDefault();
    else props.onClick?.(e);
  }, onMouseEnter: () => {
    isMouseOverRef.current = true;
    setTimeout(() => {
      if (isMouseOverRef.current) setIsHovered(true);
    }, 100);
  }, onMouseLeave: () => {
    isMouseOverRef.current = false;
    setIsHovered(false);
  }, children: [
    /* @__PURE__ */ jsxs7(AnimatePresence, { children: [
      loadingVisible && !successVisible && /* @__PURE__ */ jsx13(Overlay3, { initial: {
        opacity: 0,
        scale: 0.9
      }, animate: {
        opacity: 1,
        scale: 1
      }, exit: {
        opacity: 0,
        scale: 0.9
      }, style: {
        backgroundColor: variant == "outline" ? void 0 : "rgba(0, 0, 0, 0.1)"
      }, transition: {
        duration: 0.2,
        ease: "easeInOut"
      }, children: /* @__PURE__ */ jsx13(OverlayInner, { children: /* @__PURE__ */ jsx13(Spinner, { size: 16, foreground: buttonTheme.spinner_foreground, background: buttonTheme.spinner_background }) }) }, "loading"),
      successVisible && /* @__PURE__ */ jsx13(Overlay3, { initial: {
        opacity: 0,
        scale: 0.7
      }, animate: {
        opacity: 1,
        scale: 1
      }, exit: {
        opacity: 0,
        scale: 0.7
      }, style: {
        backgroundColor: variant == "outline" ? void 0 : "rgba(0, 0, 0, 0.1)"
      }, transition: {
        duration: 0.3,
        ease: "easeInOut"
      }, children: /* @__PURE__ */ jsx13(OverlayInner, { initial: {
        scale: 0.5,
        rotate: -15
      }, animate: {
        scale: 1,
        rotate: -10
      }, transition: {
        delay: 0.1
      }, children: /* @__PURE__ */ jsx13(Icon, { children: /* @__PURE__ */ jsx13(Check, { size: 16, strokeWidth: 3 }) }) }) }, "success")
    ] }),
    /* @__PURE__ */ jsxs7(Inner2, { style: {
      opacity: contentVisible ? 1 : 0,
      transition: "opacity 0.25s ease",
      textAlign: fullWidth || !children ? "center" : "left",
      justifyContent: fullWidth || !children ? "center" : "flex-start"
    }, "aria-hidden": loading || success, children: [
      iconLeft && /* @__PURE__ */ jsx13(Icon, { children: iconLeft }),
      children && /* @__PURE__ */ jsx13(Content7, { children }),
      iconRight && /* @__PURE__ */ jsx13(Icon, { children: iconRight })
    ] })
  ] });
});

// src/callout/index.tsx
import { RiAlertLine } from "@remixicon/react";
import { styled as styled9 } from "styled-components";

// src/callout/constants.ts
import { memo as memo4 } from "@lb-lib/memo";
var calloutColor = memo4((color) => ({
  color: theme.colors[getColorKey(color, "900")],
  backgroundColor: theme.colors[getColorKey(color, "300")]
}));
var sizes3 = {
  "1": {
    padding: "12px 16px",
    fontSize: 12,
    borderRadius: 10,
    gap: 10,
    icon: {
      size: 16
    }
  },
  "2": {
    padding: "16px 24px",
    fontSize: 14,
    borderRadius: 12,
    gap: 14,
    icon: {
      size: 18
    }
  },
  "3": {
    padding: "20px 32px",
    fontSize: 16,
    borderRadius: 14,
    gap: 18,
    icon: {
      size: 20
    }
  }
};
var getCalloutStyles = ({
  color,
  size = "2"
}) => ({
  ...calloutColor(color),
  ...sizes3[size]
});

// src/callout/index.tsx
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var Wrapper4 = styled9("div").withConfig({
  componentId: "sc-1q5vh0z-0"
})`display:flex;align-items:center;`;
var Icon2 = styled9("div").withConfig({
  componentId: "sc-1q5vh0z-1"
})`display:flex;align-items:center;justify-content:center;svg{height:100%;aspect-ratio:1;}`;
var Content8 = styled9("p").withConfig({
  componentId: "sc-1q5vh0z-2"
})``;
var Callout = ({
  children,
  color,
  size,
  style
}) => {
  let {
    icon,
    ...styles
  } = getCalloutStyles({
    color,
    size
  });
  return /* @__PURE__ */ jsxs8(Wrapper4, { style: {
    ...styles,
    ...style,
    display: "flex",
    alignItems: "center"
  }, children: [
    /* @__PURE__ */ jsx14(Icon2, { style: {
      width: icon.size,
      height: icon.size
    }, children: /* @__PURE__ */ jsx14(RiAlertLine, {}) }),
    /* @__PURE__ */ jsx14(Content8, { children })
  ] });
};

// src/cardLinks/index.tsx
import { styled as styled10 } from "styled-components";

// src/link.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var defaultLink = ({
  to,
  children,
  className
}) => {
  return /* @__PURE__ */ jsx15("a", { href: to, className, children });
};
var getLink = () => {
  let linkFromWindow = typeof window !== "undefined" ? window.LinkComponent : void 0;
  let linkFromGlobal = typeof global !== "undefined" ? global.LinkComponent : void 0;
  return linkFromWindow ?? linkFromGlobal ?? defaultLink;
};

// src/cardLinks/index.tsx
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
var LinkWrapper = styled10("span").withConfig({
  componentId: "sc-hqx8y9-0"
})`padding:20px;border-radius:10px;border:solid 1px ${theme.colors.gray400};display:flex;gap:10px;transition:all 0.2s;flex-grow:1;cursor:pointer;box-shadow:${theme.shadows.small};background:${theme.colors.background};&:hover,&:focus{border-color:${theme.colors.primary} !important;transform:scale(1.02);box-shadow:${theme.shadows.small};}&:active{transform:scale(0.98);}`;
var LinkContent = styled10("div").withConfig({
  componentId: "sc-hqx8y9-1"
})`display:flex;flex-direction:column;gap:4px;`;
var LinkTitle = styled10("p").withConfig({
  componentId: "sc-hqx8y9-2"
})`font-size:18px;font-weight:600;color:${theme.colors.foreground};`;
var LinkDescription = styled10("p").withConfig({
  componentId: "sc-hqx8y9-3"
})`font-size:14px;font-weight:500;color:${theme.colors.gray600};`;
var Icon3 = styled10("div").withConfig({
  componentId: "sc-hqx8y9-4"
})`height:40px;width:40px;display:flex;align-items:center;justify-content:center;background:${theme.colors.gray300};border-radius:5px;flex-shrink:0;transform:rotate(5deg);svg{height:20px;width:20px;transform:rotate(-5deg);}`;
var CardLinks = {
  Items: styled10("div").withConfig({
    componentId: "sc-hqx8y9-5"
  })`display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px;`,
  Group: styled10("div").withConfig({
    componentId: "sc-hqx8y9-6"
  })`display:flex;flex-direction:column;`,
  GroupPanel: styled10("div").withConfig({
    componentId: "sc-hqx8y9-7"
  })`display:flex;flex-direction:column;padding:13px 10px 10px 10px;border-radius:10px;background:${theme.colors.gray200};box-shadow:${theme.shadows.small};.link{border:solid 1px transparent;}& > h2{font-weight:700 !important;padding:0 4px;}& > p{font-weight:600;color:${theme.colors.gray700};padding:0 4px;}`,
  GroupTitle: styled10("h2").withConfig({
    componentId: "sc-hqx8y9-8"
  })`font-size:18px;font-weight:600;margin-bottom:10px;`,
  GroupPreTitle: styled10("p").withConfig({
    componentId: "sc-hqx8y9-9"
  })`font-size:14px;font-weight:600;margin-bottom:4px;color:${theme.colors.gray700};`,
  Link: ({
    to,
    title,
    description
  }) => {
    let Link = getLink();
    return /* @__PURE__ */ jsx16(Link, { to, style: {
      textDecoration: "none",
      color: "inherit",
      display: "flex",
      flexDirection: "column"
    }, children: /* @__PURE__ */ jsx16(LinkWrapper, { className: "link", children: /* @__PURE__ */ jsxs9(LinkContent, { children: [
      /* @__PURE__ */ jsx16(LinkTitle, { children: title }),
      /* @__PURE__ */ jsx16(LinkDescription, { children: description })
    ] }) }) });
  }
};

// src/checkbox/index.tsx
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as VisuallyHidden2 from "@radix-ui/react-visually-hidden";
import clsx from "clsx";
import { useEffect as useEffect6, useId as useId2, useState as useState9 } from "react";
import { Check as Check2 } from "react-feather";
import { keyframes as keyframes5, styled as styled13 } from "styled-components";

// src/input/index.tsx
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useId } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { styled as styled12 } from "styled-components";

// src/error/index.tsx
import { RiErrorWarningLine } from "@remixicon/react";
import { styled as styled11 } from "styled-components";

// src/theme/calc.ts
var calc = {
  add: (a, b) => `calc(${a} + ${b})`,
  subtract: (a, b) => `calc(${a} - ${b})`,
  multiply: (a, b) => `calc(${a} * ${b})`,
  divide: (a, b) => `calc(${a} / ${b})`
};

// src/error/index.tsx
import { jsx as jsx17, jsxs as jsxs10 } from "react/jsx-runtime";
var Wrapper5 = styled11("p").withConfig({
  componentId: "sc-188qotz-0"
})`display:flex;gap:7px;align-items:center;color:${theme.colors.red900};font-weight:500;`;
var Error = ({
  size,
  children,
  style
}) => {
  let sizeString = (typeof size == "number" ? `${size}px` : size) || "14px";
  return /* @__PURE__ */ jsxs10(Wrapper5, { style: {
    fontSize: sizeString,
    ...style
  }, children: [
    /* @__PURE__ */ jsx17("span", { style: {
      display: "inline-flex"
    }, children: /* @__PURE__ */ jsx17(RiErrorWarningLine, { size: calc.multiply(sizeString, 1.3) }) }),
    /* @__PURE__ */ jsx17("span", { children })
  ] });
};

// src/input/index.tsx
import { jsx as jsx18, jsxs as jsxs11 } from "react/jsx-runtime";
var Wrapper6 = styled12("div").withConfig({
  componentId: "sc-1tq9ngj-0"
})`display:flex;flex-direction:column;align-items:flex-start;width:100%;`;
var InputLabel = styled12("label").withConfig({
  componentId: "sc-1tq9ngj-1"
})`font-size:12px;color:${theme.colors.gray900};margin-bottom:5px;font-weight:500;user-select:none;white-space:nowrap;`;
var InputDescription = styled12("p").withConfig({
  componentId: "sc-1tq9ngj-2"
})`font-size:12px;color:${theme.colors.gray600};margin-bottom:8px;user-select:none;`;
var InputWrapper = styled12("div").withConfig({
  componentId: "sc-1tq9ngj-3"
})`outline:1px solid transparent;background:${theme.colors.gray200};color:${theme.colors.foreground};display:flex;width:100%;transition:all 0.3s ease;&:focus-within{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}`;
var InputField = styled12("input").withConfig({
  componentId: "sc-1tq9ngj-4"
})`flex-grow:1;border:none;background:transparent;height:100%;width:100%;outline:none;&::placeholder{color:${theme.colors.gray700};}&[type='number']{-moz-appearance:textfield;}&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}`;
var TextareaField = styled12(TextareaAutosize).withConfig({
  componentId: "sc-1tq9ngj-5"
})`flex-grow:1;border:none;background:transparent;height:100%;width:100%;outline:none;resize:none;&::placeholder{color:${theme.colors.gray700};}`;
var Input = ({
  style,
  size = "3",
  label,
  description,
  hideLabel,
  error,
  as = "input",
  minRows,
  maxRows,
  onInput,
  ...props
}) => {
  let sizeStyles = getButtonSize(size);
  let id = useId();
  return /* @__PURE__ */ jsxs11(Wrapper6, { style, children: [
    hideLabel ? /* @__PURE__ */ jsx18(VisuallyHidden, { children: /* @__PURE__ */ jsx18(InputLabel, { htmlFor: id, children: label }) }) : /* @__PURE__ */ jsx18(InputLabel, { htmlFor: id, children: label }),
    description && /* @__PURE__ */ jsx18(InputDescription, { children: description }),
    /* @__PURE__ */ jsx18(InputWrapper, { style: {
      height: as == "textarea" ? void 0 : sizeStyles.height,
      borderRadius: sizeStyles.borderRadius,
      outline: error ? `1px solid ${theme.colors.red900}` : void 0,
      paddingBlock: as == "textarea" ? calc.divide(sizeStyles.height, 3.7) : void 0
    }, children: as == "textarea" ? /* @__PURE__ */ jsx18(TextareaField, { id, minRows, maxRows, style: {
      padding: sizeStyles.padding,
      fontSize: "14px"
    }, onChange: (e) => {
      onInput?.(e.target.value, e);
      props.onChange?.(e);
    }, ...props }) : /* @__PURE__ */ jsx18(InputField, { id, style: {
      padding: sizeStyles.padding,
      height: sizeStyles.height,
      fontSize: "14px"
    }, onChange: (e) => {
      onInput?.(e.target.value, e);
      props.onChange?.(e);
    }, ...props }) }),
    typeof error == "string" && /* @__PURE__ */ jsx18(Error, { size: 12, style: {
      marginTop: 6
    }, children: error })
  ] });
};

// src/checkbox/index.tsx
import { Fragment as Fragment2, jsx as jsx19, jsxs as jsxs12 } from "react/jsx-runtime";
var fadeIn2 = keyframes5`
  from {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }

  to {
    transform: scale(1) rotate(-10deg);
    opacity: 1;
  }
`;
var fadeOut2 = keyframes5`
  from {
    transform: scale(1);
    opacity: 1;
  }

  to {
    transform: scale(0.5);
    opacity: 0;
  }
`;
var Wrapper7 = styled13("div").withConfig({
  componentId: "sc-11tc7ji-0"
})`display:flex;align-items:center;cursor:pointer;width:100%;`;
var Root6 = styled13(RadixCheckbox.Root).withConfig({
  componentId: "sc-11tc7ji-1"
})`display:flex;height:20px;width:20px;border:none;padding:0px;justify-content:center;align-items:center;background:${theme.colors.gray100};border:1px solid ${theme.colors.gray300};border-radius:5px;color:${theme.colors.gray100};transition:all 0.3s ease;position:relative;overflow:hidden;flex-shrink:0;&.disabled{opacity:0.5;}&::before{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:0px;height:0px;content:'';border-radius:50%;background:${theme.colors.gray800};transition:all 0.3s ease;}&[data-state='checked']{box-shadow:${theme.shadows.small};&::before{width:30px;height:30px;}}`;
var Indicator2 = styled13(RadixCheckbox.Indicator).withConfig({
  componentId: "sc-11tc7ji-2"
})`display:flex;justify-content:center;align-items:center;&[data-animate='true'][data-state='checked']{animation:${fadeIn2} 0.4s ease forwards;}&[data-animate='true'][data-state='unchecked']{animation:${fadeOut2} 0.3s ease forwards;}&[data-animate='false'][data-state='checked']{transform:scale(1) rotate(-10deg) !important;opacity:1 !important;}`;
var Checkbox = ({
  checked,
  onCheckedChange,
  description,
  label,
  disabled,
  hideLabel
}) => {
  let [animate, setAnimate] = useState9(false);
  let [initialValue] = useState9(() => checked);
  useEffect6(() => {
    if (initialValue != checked) setAnimate(true);
  }, [checked, initialValue]);
  let id = useId2();
  return /* @__PURE__ */ jsx19(Fragment2, { children: /* @__PURE__ */ jsxs12(Wrapper7, { children: [
    /* @__PURE__ */ jsx19(Root6, { className: clsx({
      disabled
    }), id, checked, onCheckedChange, disabled, children: /* @__PURE__ */ jsx19(Indicator2, { "data-animate": animate, children: /* @__PURE__ */ jsx19(Check2, { size: 12, strokeWidth: 3 }) }) }),
    !hideLabel ? /* @__PURE__ */ jsxs12("div", { style: {
      margin: 0,
      width: "100%",
      minHeight: 20,
      paddingLeft: 10,
      display: "flex",
      justifyContent: "center",
      gap: 5,
      cursor: "pointer",
      flexDirection: "column"
    }, children: [
      /* @__PURE__ */ jsx19(InputLabel, { htmlFor: id, style: {
        margin: 0
      }, children: label }),
      description && /* @__PURE__ */ jsx19(InputDescription, { style: {
        margin: 0
      }, children: description })
    ] }) : /* @__PURE__ */ jsx19(VisuallyHidden2.Root, { children: /* @__PURE__ */ jsx19("label", { htmlFor: id, children: label }) })
  ] }) });
};

// src/control/index.tsx
import * as RadixToggle from "@radix-ui/react-toggle-group";
import { memo as memo5, useEffect as useEffect7, useReducer, useState as useState10 } from "react";
import { styled as styled14 } from "styled-components";
import { jsx as jsx20, jsxs as jsxs13 } from "react/jsx-runtime";
var Root8 = styled14(RadixToggle.Root).withConfig({
  componentId: "sc-s5vh2e-0"
})`background:${theme.colors.gray300};border-radius:10px;width:fit-content;padding:3px;`;
var List = styled14("div").withConfig({
  componentId: "sc-s5vh2e-1"
})`display:flex;position:relative;`;
var Item4 = styled14(RadixToggle.Item).withConfig({
  componentId: "sc-s5vh2e-2"
})`display:flex;align-items:center;border:none;background:transparent;padding:0px;font-size:14px;color:${theme.colors.gray600};transition:all 0.3s ease;position:relative;z-index:2;font-weight:500;&:hover{color:${theme.colors.blue900};}&:focus{color:${theme.colors.blue900};}&[data-state='active']{color:${theme.colors.gray100};}`;
var ItemInner = styled14("div").withConfig({
  componentId: "sc-s5vh2e-3"
})`height:30px;padding:0px 10px;display:flex;align-items:center;justify-content:center;`;
var Indicator3 = styled14("div").withConfig({
  componentId: "sc-s5vh2e-4"
})`position:absolute;border-radius:7px;z-index:1;background:${theme.colors.blue900};transition:all 0.2s cubic-bezier(0.34,1.56,0.64,1);`;
var Control = memo5(({
  items,
  value,
  onChange
}) => {
  let [elements, setElements] = useState10([]);
  let currentItemIndex = items.findIndex((item) => item.id == value);
  let [, rerender] = useReducer((s) => s + 1, 0);
  useEffect7(() => {
    setTimeout(() => {
      rerender();
      setTimeout(() => {
        rerender();
      }, 400);
    }, 100);
  }, []);
  return /* @__PURE__ */ jsx20(Root8, { value, onValueChange: (v) => {
    if (v == value || !v) return;
    onChange?.(v);
  }, type: "single", children: /* @__PURE__ */ jsxs13(List, { children: [
    items.map((item, i) => /* @__PURE__ */ jsx20(Item4, { value: item.id, "data-state": item.id == value ? "active" : "inactive", ref: (el) => {
      if (el) {
        setElements((e) => {
          e[i] = el;
          return e;
        });
      }
    }, children: /* @__PURE__ */ jsx20(ItemInner, { children: item.label }) }, i)),
    elements[currentItemIndex] && /* @__PURE__ */ jsx20(Indicator3, { style: {
      height: 30,
      top: 0,
      left: elements[currentItemIndex].offsetLeft,
      width: elements[currentItemIndex].offsetWidth
    } })
  ] }) });
});

// src/copy/index.tsx
import { RiCheckLine } from "@remixicon/react";
import copy from "copy-to-clipboard";
import { AnimatePresence as AnimatePresence2, motion as motion5 } from "framer-motion";
import { useRef as useRef5, useState as useState11 } from "react";
import { toast } from "sonner";
import { styled as styled15 } from "styled-components";
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var Value = styled15("pre").withConfig({
  componentId: "sc-fx5cnk-0"
})`white-space:pre-wrap;word-wrap:break-word;margin:0;display:block;font-size:14px;padding:10px;border-radius:8px;background:${theme.colors.gray200};color:${theme.colors.foreground};`;
var CopyOverlay = styled15(motion5.div).withConfig({
  componentId: "sc-fx5cnk-1"
})`position:absolute;top:0;right:0;left:0;bottom:0;padding:10px;border-radius:8px;background:rgba(255,255,255,0.8);border:1px solid ${theme.colors.gray300};z-index:1;display:flex;justify-content:center;align-items:center;`;
var CopyIcon = styled15(motion5.div).withConfig({
  componentId: "sc-fx5cnk-2"
})`color:white;background:${theme.colors.gray800};height:26px;width:26px;border-radius:20px;display:flex;justify-content:center;align-items:center;`;
var useCopy = (topVal) => {
  let [copied, setCopied] = useState11(false);
  let topValRef = useRef5(topVal);
  topValRef.current = topVal;
  let doCopy = (innerVal) => {
    let value = innerVal || topValRef.current;
    if (!value) return;
    if (copied) return;
    copy(value);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2e3);
  };
  return {
    copied,
    copy: doCopy
  };
};
var Copy = ({
  value,
  label
}) => {
  let {
    copied,
    copy: doCopy
  } = useCopy(value);
  return /* @__PURE__ */ jsxs14("div", { onClick: (c) => doCopy(), role: "button", onKeyDown: (c) => doCopy(), tabIndex: 0, style: {
    display: "flex",
    flexDirection: "column"
  }, children: [
    label && /* @__PURE__ */ jsx21(InputLabel, { children: label }),
    /* @__PURE__ */ jsxs14("div", { style: {
      position: "relative"
    }, children: [
      /* @__PURE__ */ jsx21(Value, { children: value }),
      /* @__PURE__ */ jsx21(AnimatePresence2, { children: copied && /* @__PURE__ */ jsx21(CopyOverlay, { initial: {
        opacity: 0
      }, animate: {
        opacity: 1
      }, exit: {
        opacity: 0
      }, transition: {
        duration: 0.2
      }, children: /* @__PURE__ */ jsx21(CopyIcon, { initial: {
        scale: 0,
        rotate: -30
      }, animate: {
        scale: 1,
        rotate: -10
      }, transition: {
        duration: 0.3,
        delay: 0.1
      }, children: /* @__PURE__ */ jsx21(RiCheckLine, { size: 12, strokeWidth: 3 }) }) }) })
    ] })
  ] });
};

// src/datalist/index.tsx
import { Fragment as Fragment3 } from "react";
import { styled as styled16 } from "styled-components";
import { jsx as jsx22, jsxs as jsxs15 } from "react/jsx-runtime";
var Wrapper8 = styled16("div").withConfig({
  componentId: "sc-h73mtj-0"
})``;
var DL = styled16("dl").withConfig({
  componentId: "sc-h73mtj-1"
})`display:grid;grid-template-columns:auto 1fr;gap:10px;margin:0;padding:0;font-size:14px;`;
var DT = styled16("dt").withConfig({
  componentId: "sc-h73mtj-2"
})`font-weight:600;color:${theme.colors.gray600};`;
var DD = styled16("dd").withConfig({
  componentId: "sc-h73mtj-3"
})`font-weight:400;`;
var Datalist = ({
  items
}) => {
  return /* @__PURE__ */ jsx22(Wrapper8, { children: /* @__PURE__ */ jsx22(DL, { children: items.map((item, i) => /* @__PURE__ */ jsxs15(Fragment3, { children: [
    /* @__PURE__ */ jsx22(DT, { children: item.label }),
    /* @__PURE__ */ jsx22(DD, { children: item.value })
  ] }, i)) }) });
};

// src/date/index.tsx
import { useMemo as useMemo3 } from "react";
import { shleemy } from "shleemy";
import { styled as styled17 } from "styled-components";
import { jsx as jsx23, jsxs as jsxs16 } from "react/jsx-runtime";
var Header = styled17("h1").withConfig({
  componentId: "sc-lceqfp-0"
})`font-size:14px;font-weight:600;`;
var Description3 = styled17("p").withConfig({
  componentId: "sc-lceqfp-1"
})`font-size:13px;margin-top:5px;opacity:0.6;font-weight:500;strong{font-weight:700;}`;
var RenderDate = ({
  date,
  size,
  weight
}) => {
  let result = useMemo3(() => {
    if (!date) return {
      date: null,
      utc: "",
      local: "",
      pretty: "",
      timeZone: ""
    };
    let parsed = typeof date == "string" ? new Date(date) : date;
    let utc = new Date(parsed.toUTCString().replace("GMT", ""));
    let humanOffset = shleemy(parsed).forHumans;
    return {
      date: parsed,
      humanOffset,
      utc: utc.toLocaleString(),
      local: parsed.toLocaleString(),
      pretty: parsed.toLocaleString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }, [date]);
  if (!result.date) return /* @__PURE__ */ jsx23("time", { style: {
    cursor: "pointer"
  }, children: "No date" });
  return /* @__PURE__ */ jsx23(Tooltip, { content: /* @__PURE__ */ jsxs16("div", { style: {
    padding: 5
  }, children: [
    /* @__PURE__ */ jsx23(Header, { children: "Time" }),
    /* @__PURE__ */ jsxs16(Description3, { children: [
      /* @__PURE__ */ jsx23("strong", { children: "UTC" }),
      ": ",
      /* @__PURE__ */ jsx23("time", { children: result.utc })
    ] }),
    /* @__PURE__ */ jsxs16(Description3, { children: [
      /* @__PURE__ */ jsx23("strong", { children: "Local" }),
      ": ",
      /* @__PURE__ */ jsx23("time", { children: result.local }),
      " (",
      result.timeZone,
      ")"
    ] }),
    /* @__PURE__ */ jsxs16(Description3, { children: [
      /* @__PURE__ */ jsx23("strong", { children: "Offset" }),
      ": ",
      /* @__PURE__ */ jsx23("time", { children: result.humanOffset })
    ] })
  ] }), children: /* @__PURE__ */ jsx23("div", { style: {
    width: "fit-content"
  }, children: /* @__PURE__ */ jsx23(Text, { style: {
    cursor: "pointer",
    borderBottom: `1px dashed ${theme.colors.gray500}`,
    margin: 0,
    padding: 0,
    whiteSpace: "nowrap",
    lineHeight: void 0,
    ...!size && {
      fontSize: 12
    }
  }, size, weight, children: result.pretty }) }) });
};

// src/datePicker/index.tsx
import * as RadixPopover from "@radix-ui/react-popover";
import { VisuallyHidden as VisuallyHidden3 } from "@radix-ui/react-visually-hidden";
import { RiArrowLeftSFill, RiArrowRightSLine } from "@remixicon/react";
import { useState as useState13 } from "react";
import { keyframes as keyframes6, styled as styled18 } from "styled-components";

// src/datePicker/state.ts
import { addMonths, eachDayOfInterval, eachWeekOfInterval, endOfDay, endOfMonth, endOfWeek, isAfter, isBefore, isSameDay, isSameMonth, isToday, isWithinInterval, startOfDay, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { useEffect as useEffect8, useMemo as useMemo4, useState as useState12 } from "react";
var dateToRange = (date) => {
  if (Array.isArray(date)) return date;
  return [date, date];
};
var useDatePickerState = ({
  value,
  onChange,
  type,
  open,
  maxDate,
  minDate
}) => {
  let [selectedDate, setSelectedDate] = useState12(() => value ? dateToRange(value) : void 0);
  useEffect8(() => {
    setSelectedDate(value ? dateToRange(value) : void 0);
  }, [value, open]);
  let [currentMonth, setCurrentMonth] = useState12(() => startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
  useEffect8(() => {
    let start = startOfWeek(startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
    let end = endOfWeek(endOfMonth(selectedDate ? selectedDate[1] : /* @__PURE__ */ new Date()));
    if (!isWithinInterval(currentMonth, {
      start,
      end
    })) {
      setCurrentMonth(startOfMonth(selectedDate ? selectedDate[0] : /* @__PURE__ */ new Date()));
    }
  }, [selectedDate]);
  let prevMonthEnabled = !minDate || isAfter(currentMonth, minDate);
  let nextMonthEnabled = !maxDate || isBefore(addMonths(currentMonth, 1), maxDate);
  let nextMonth = () => setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)));
  let prevMonth = () => setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)));
  let days = useMemo4(() => {
    let startDate = currentMonth;
    let endDate = endOfMonth(currentMonth);
    let weeks = eachWeekOfInterval({
      start: startDate,
      end: endDate
    });
    return weeks.map((week) => {
      return {
        id: `week_${week.getTime()}`,
        days: eachDayOfInterval({
          start: startOfWeek(week),
          end: endOfWeek(week)
        }).map((day) => {
          let isInSelectedRange = !!(selectedDate && isWithinInterval(day, {
            start: selectedDate[0],
            end: selectedDate[1]
          }));
          let isStartOfRange = !!selectedDate && isSameDay(day, selectedDate[0]);
          let isEndOfRange = !!selectedDate && isSameDay(day, selectedDate[1]);
          return {
            id: `day_${day.getTime()}`,
            date: day,
            isToday: isToday(day),
            isInSelectedRange,
            isSelected: isStartOfRange || isEndOfRange,
            isStartOfRange,
            isEndOfRange,
            isCurrentMonth: isSameMonth(day, currentMonth)
          };
        })
      };
    });
  }, [currentMonth, selectedDate]);
  let selectDate = (date) => {
    if (type == "single") {
      let range = dateToRange(date);
      setSelectedDate(range);
      onChange(range[0]);
    } else {
      let range;
      if (Array.isArray(date)) {
        range = date;
      } else {
        if (!selectedDate || isSameDay(date, selectedDate[0]) || isSameDay(date, selectedDate[1])) {
          range = [date, date];
        } else if (isBefore(date, selectedDate[0])) {
          range = [date, selectedDate[1]];
        } else if (isAfter(date, selectedDate[1])) {
          range = [selectedDate[0], date];
        } else {
          range = [selectedDate[0], date];
        }
      }
      let normalizedRange = [startOfDay(range[0]), endOfDay(range[1])];
      setSelectedDate(normalizedRange);
      onChange(normalizedRange);
    }
  };
  let stringValue = useMemo4(() => {
    if (!selectedDate) return void 0;
    if (type == "single") {
      return selectedDate[0].toLocaleDateString();
    } else {
      return selectedDate.map((d) => d.toLocaleDateString()).join(" - ");
    }
  }, [selectedDate, type]);
  let reset = () => {
    setSelectedDate(void 0);
    onChange(void 0);
  };
  return {
    currentMonth,
    nextMonth,
    prevMonth,
    days,
    selectDate,
    stringValue,
    reset,
    nextMonthEnabled,
    prevMonthEnabled
  };
};

// src/datePicker/index.tsx
import { jsx as jsx24, jsxs as jsxs17 } from "react/jsx-runtime";
var fadeIn3 = keyframes6`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var fadeOut3 = keyframes6`
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;
var Trigger5 = styled18(RadixPopover.Trigger).withConfig({
  componentId: "sc-1a5m52k-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray200};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:all 0.3s ease;border:none;font-weight:500;&:focus-within,&:focus{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}& [data-placeholder]{color:${theme.colors.gray700};font-weight:400;}&:disabled{cursor:not-allowed;opacity:0.7;}`;
var Content10 = styled18(RadixPopover.Content).withConfig({
  componentId: "sc-1a5m52k-1"
})`background:${theme.colors.background};border:1px solid ${theme.colors.gray300};border-radius:10px;box-shadow:${theme.shadows.medium};overflow:hidden;z-index:9999;display:flex;flex-direction:column;pointer-events:auto;&[data-state='open']{animation:${fadeIn3} 0.15s ease-in-out;}&[data-state='closed']{animation:${fadeOut3} 0.15s ease-in-out;}`;
var Header2 = styled18("header").withConfig({
  componentId: "sc-1a5m52k-2"
})`display:flex;justify-content:space-between;padding:10px;align-items:center;border-bottom:1px solid ${theme.colors.gray300};`;
var Month = styled18("div").withConfig({
  componentId: "sc-1a5m52k-3"
})`display:flex;flex-direction:column;padding:10px;gap:2px;`;
var Week = styled18("div").withConfig({
  componentId: "sc-1a5m52k-4"
})`display:grid;grid-template-columns:repeat(7,1fr);gap:2px;`;
var Day = styled18("button").withConfig({
  componentId: "sc-1a5m52k-5"
})`display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:5px;cursor:pointer;background:transparent;color:${theme.colors.gray800};border:none;transition:all 0.3s ease;font-weight:500;font-size:14px;&[data-today='true']{background:${theme.colors.blue100};color:${theme.colors.blue700};}&[data-in-range='true']{background:${theme.colors.blue100};color:${theme.colors.blue800};}&[data-selected='true']{background:${theme.colors.blue400};color:${theme.colors.blue900};}&[data-not-in-month='true']{opacity:0.5;}&:disabled{cursor:not-allowed;opacity:0.5;}`;
var Footer = styled18("footer").withConfig({
  componentId: "sc-1a5m52k-6"
})`display:flex;justify-content:flex-end;padding:0px 10px 10px 10px;gap:10px;`;
var WeekDay = styled18("div").withConfig({
  componentId: "sc-1a5m52k-7"
})`text-align:center;font-size:12px;padding-bottom:5px;color:${theme.colors.gray600};font-weight:500;`;
var DatePicker = (props) => {
  let state = useDatePickerState(props);
  let [open, setOpen] = useState13(false);
  let sizeStyles = getButtonSize(props.size ?? "3");
  return /* @__PURE__ */ jsx24("div", { children: /* @__PURE__ */ jsxs17(RadixPopover.Root, { open, onOpenChange: setOpen, children: [
    props.hideLabel ? /* @__PURE__ */ jsx24(VisuallyHidden3, { children: /* @__PURE__ */ jsx24("label", { children: props.label }) }) : /* @__PURE__ */ jsx24(InputLabel, { children: props.label }),
    props.description && /* @__PURE__ */ jsx24(InputDescription, { children: props.description }),
    /* @__PURE__ */ jsx24(Trigger5, { style: {
      ...sizeStyles,
      fontSize: 14
    }, disabled: props.disabled, children: state.stringValue || /* @__PURE__ */ jsx24("span", { "data-placeholder": true, children: props.placeholder }) }),
    /* @__PURE__ */ jsx24(RadixPopover.Portal, { children: /* @__PURE__ */ jsxs17(Content10, { sideOffset: 5, side: "bottom", children: [
      /* @__PURE__ */ jsxs17(Header2, { children: [
        /* @__PURE__ */ jsx24(Tooltip, { content: "Previous Month", children: /* @__PURE__ */ jsx24(Button, { size: "2", variant: "soft", iconLeft: /* @__PURE__ */ jsx24(RiArrowLeftSFill, {}), onClick: state.prevMonth, disabled: !state.prevMonthEnabled }) }),
        /* @__PURE__ */ jsx24(AnimateContent, { identifier: state.currentMonth.getTime(), children: /* @__PURE__ */ jsx24(Title3, { as: "h1", weight: "bold", size: "2", align: "center", children: state.currentMonth.toLocaleDateString() }) }),
        /* @__PURE__ */ jsx24(Tooltip, { content: "Next Month", children: /* @__PURE__ */ jsx24(Button, { size: "2", variant: "soft", iconLeft: /* @__PURE__ */ jsx24(RiArrowRightSLine, {}), onClick: state.nextMonth, disabled: !state.nextMonthEnabled }) })
      ] }),
      /* @__PURE__ */ jsx24(AnimatePanes, { orderedIdentifier: state.currentMonth.getTime(), children: /* @__PURE__ */ jsxs17(Month, { children: [
        /* @__PURE__ */ jsx24(Week, { children: state.days[0].days.map((day) => /* @__PURE__ */ jsx24(WeekDay, { children: day.date.toLocaleDateString("en", {
          weekday: "short"
        }) }, day.id)) }),
        state.days.map((week) => /* @__PURE__ */ jsx24(Week, { children: week.days.map((day) => /* @__PURE__ */ jsx24(Day, { "data-today": day.isToday, "data-in-range": day.isInSelectedRange, "data-selected": day.isSelected, "data-not-in-month": !day.isCurrentMonth, onClick: () => state.selectDate(day.date), disabled: props.maxDate && day.date > props.maxDate || props.minDate && day.date < props.minDate, children: day.date.getDate() }, day.id)) }, week.id))
      ] }) }),
      /* @__PURE__ */ jsxs17(Footer, { children: [
        props.resettable && /* @__PURE__ */ jsx24(Button, { size: "2", variant: "soft", onClick: () => {
          state.reset();
        }, children: "Reset" }),
        /* @__PURE__ */ jsx24(RadixPopover.Close, { asChild: true, children: /* @__PURE__ */ jsx24(Button, { size: "2", variant: "soft", autoFocus: true, onClick: () => {
          setOpen(false);
        }, children: "Done" }) })
      ] })
    ] }) })
  ] }) });
};

// src/dialog/dialog.tsx
import * as RadixDialogDialog2 from "@radix-ui/react-dialog";
import { RiCloseLine } from "@remixicon/react";
import { styled as styled20 } from "styled-components";

// src/dialog/styles.ts
import * as RadixDialogDialog from "@radix-ui/react-dialog";
import { keyframes as keyframes7, styled as styled19 } from "styled-components";
var fadeInShift2 = keyframes7`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;
var fadeOutShift2 = keyframes7`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
`;
var fadeIn4 = keyframes7`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
var fadeOut4 = keyframes7`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
var Overlay5 = styled19(RadixDialogDialog.Overlay).withConfig({
  componentId: "sc-1dtq9o5-0"
})`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.1);backdrop-filter:blur(5px);&[data-state='open']{animation:${fadeIn4} 200ms ease-out forwards;}&[data-state='closed']{animation:${fadeOut4} 200ms ease-in forwards;}`;
var Content12 = styled19(RadixDialogDialog.Content).withConfig({
  componentId: "sc-1dtq9o5-1"
})`position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:${theme.colors.background};border-radius:10px;box-shadow:${theme.shadows.large};color:${theme.colors.gray900};padding:20px;&[data-state='open']{animation:${fadeInShift2} 200ms ease-out;}&[data-state='closed']{animation:${fadeOutShift2} 200ms ease-in;}`;
var Close2 = styled19("button").withConfig({
  componentId: "sc-1dtq9o5-2"
})`position:absolute;top:15px;right:15px;height:26px;width:26px;display:flex;align-items:center;justify-content:center;padding:0;border-radius:50%;border:none;transition:background 0.3s ease;background:${theme.colors.gray300};&:hover{background:${theme.colors.gray400};}`;

// src/dialog/dialog.tsx
import { jsx as jsx25, jsxs as jsxs18 } from "react/jsx-runtime";
var Wrapper9 = styled20(Content12).withConfig({
  componentId: "sc-d52ulu-0"
})`background:${theme.colors.gray300};border-radius:11px;width:90vw;max-height:90vh;overflow:auto;padding:7px;`;
var Inner3 = styled20("div").withConfig({
  shouldForwardProp: (prop) => prop !== "variant"
}).withConfig({
  componentId: "sc-d52ulu-1"
})`background:${theme.colors.background};box-shadow:0 0 10px rgba(0,0,0,0.1);color:${theme.colors.gray900};padding:20px;border-radius:9px;box-shadow:${theme.shadows.medium};${({
  variant
}) => variant === "padded" ? `
    padding: 120px 60px;

    @media (max-width: 800px) {
      padding: 20px;
    }
  ` : `
    padding: 20px;
  `}`;
var Title4 = styled20("h1").withConfig({
  componentId: "sc-d52ulu-2"
})`padding:0;font-size:20px;font-weight:600;padding:0px 0px 6px 0px;`;
var Description4 = styled20("p").withConfig({
  componentId: "sc-d52ulu-3"
})`padding:0px 0px 20px 0px;font-size:14px;font-weight:500;color:${theme.colors.gray600};`;
var Actions2 = styled20("div").withConfig({
  componentId: "sc-d52ulu-4"
})`display:flex;justify-content:flex-end;gap:10px;padding:0;`;
var Dialog = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    variant = "slim",
    width
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    return /* @__PURE__ */ jsx25(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit
    }, children: /* @__PURE__ */ jsx25(RadixDialogDialog2.Root, { open: isOpen, onOpenChange, children: /* @__PURE__ */ jsxs18(RadixDialogDialog2.Portal, { children: [
      /* @__PURE__ */ jsx25(Overlay5, { style: {
        zIndex
      } }),
      /* @__PURE__ */ jsx25(Wrapper9, { style: {
        ...style,
        zIndex: zIndex + 1,
        maxWidth: width ? width : variant == "padded" ? 480 : 400
      }, children: /* @__PURE__ */ jsxs18(Inner3, { variant, children: [
        children,
        /* @__PURE__ */ jsx25(RadixDialogDialog2.Close, { asChild: true, children: /* @__PURE__ */ jsx25(Close2, { "aria-label": "Close", children: /* @__PURE__ */ jsx25(RiCloseLine, { size: 16 }) }) })
      ] }) })
    ] }) }) });
  },
  Title: ({
    children
  }) => {
    return /* @__PURE__ */ jsx25(Title4, { children });
  },
  Description: ({
    children
  }) => {
    return /* @__PURE__ */ jsx25(Description4, { children });
  },
  Actions: ({
    children
  }) => {
    return /* @__PURE__ */ jsx25(Actions2, { children });
  }
};

// src/dialog/largePanel.tsx
import * as RadixDialogDialog3 from "@radix-ui/react-dialog";
import { RiCloseLine as RiCloseLine2 } from "@remixicon/react";
import { styled as styled21 } from "styled-components";
import { jsx as jsx26, jsxs as jsxs19 } from "react/jsx-runtime";
var PanelContent = styled21(Content12).withConfig({
  componentId: "sc-gkozsz-0"
})`max-width:90vw;width:min(max(400px,80vw),1100px);min-height:min(70vh,850px);max-height:90vh;padding:0;overflow:auto;`;
var LargePanelDialog = {
  Wrapper: ({
    children,
    isOpen,
    onOpenChange,
    style,
    autoCloseOnSubmit,
    closeButton = true
  }) => {
    let zIndex = useDialogZIndex(isOpen);
    return /* @__PURE__ */ jsx26(DialogProvider, { value: {
      isOpen,
      onOpenChange,
      autoCloseOnSubmit
    }, children: /* @__PURE__ */ jsx26(RadixDialogDialog3.Root, { open: isOpen, onOpenChange, children: /* @__PURE__ */ jsxs19(RadixDialogDialog3.Portal, { children: [
      /* @__PURE__ */ jsx26(Overlay5, { style: {
        zIndex
      } }),
      /* @__PURE__ */ jsxs19(PanelContent, { style: {
        ...style,
        zIndex: zIndex + 1
      }, children: [
        children,
        closeButton && /* @__PURE__ */ jsx26(RadixDialogDialog3.Close, { asChild: true, children: /* @__PURE__ */ jsx26(Close2, { "aria-label": "Close", children: /* @__PURE__ */ jsx26(RiCloseLine2, { size: 16 }) }) })
      ] })
    ] }) }) });
  }
};

// src/entity/content.tsx
import { styled as styled22 } from "styled-components";
import { jsx as jsx27 } from "react/jsx-runtime";
var Wrapper10 = styled22("main").withConfig({
  componentId: "sc-zl50uu-0"
})`display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;padding:0px 20px;@media (max-width:600px){padding:0px;flex-direction:column;flex-wrap:nowrap;}&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var ContentEntityWrapper = styled22("div").withConfig({
  componentId: "sc-zl50uu-1"
})`display:flex;flex-grow:1;`;
var MobileBorderStyle = styled22(ContentEntityWrapper).withConfig({
  componentId: "sc-zl50uu-2"
})`@media (max-width:600px){border-bottom:1px solid ${theme.colors.gray300};}`;
var ContentEntity = ({
  children,
  last
}) => {
  let Element = last ? ContentEntityWrapper : MobileBorderStyle;
  return /* @__PURE__ */ jsx27(Element, { children });
};
var EntityContent = ({
  children
}) => {
  let childArray = Array.isArray(children) ? children : [children];
  return /* @__PURE__ */ jsx27(Wrapper10, { children: childArray.map((child, i) => /* @__PURE__ */ jsx27(ContentEntity, { last: i == childArray.length - 1, children: child }, i)) });
};

// src/entity/contentRaw.tsx
import { styled as styled23 } from "styled-components";
var EntityContentRaw = styled23("section").withConfig({
  componentId: "sc-1u5d8l8-0"
})`padding:10px 20px;display:flex;flex-direction:column;flex-wrap:wrap;flex:1;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;

// src/entity/field.tsx
import { styled as styled24 } from "styled-components";

// src/spacer/index.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var Spacer = ({
  size,
  height,
  width,
  grow,
  shrink = false
}) => {
  return /* @__PURE__ */ jsx28("div", { style: {
    height: height ?? size,
    width: width ?? size,
    flexGrow: !height && !width && !size || grow ? 1 : void 0,
    flexShrink: shrink === false ? 0 : shrink ? 1 : void 0
  } });
};

// src/entity/field.tsx
import { Fragment as Fragment4, jsx as jsx29, jsxs as jsxs20 } from "react/jsx-runtime";
var Wrapper11 = styled24("div").withConfig({
  componentId: "sc-15khj0k-0"
})`display:flex;padding:20px 0px;align-items:center;@media (max-width:600px){padding:20px;}`;
var Content13 = styled24("div").withConfig({
  componentId: "sc-15khj0k-1"
})`display:flex;flex-direction:column;`;
var Title5 = styled24("h1").withConfig({
  componentId: "sc-15khj0k-2"
})`font-size:14px;font-weight:600;line-height:1;`;
var Description5 = styled24("p").withConfig({
  componentId: "sc-15khj0k-3"
})`font-size:12px;font-weight:400;color:${theme.colors.gray700};margin-top:4px;line-height:1;`;
var EntityField = ({
  title,
  description,
  value,
  width,
  children,
  position,
  style,
  right,
  prefix,
  suffix,
  prefixNoSpace,
  suffixNoSpace,
  skeleton
}) => {
  if (!position) position = "grow";
  if (!description) description = value;
  return /* @__PURE__ */ jsxs20(Wrapper11, { style: {
    width,
    flex: position == "grow" ? 1 : void 0,
    textAlign: right ? "right" : void 0,
    ...style
  }, children: [
    right && /* @__PURE__ */ jsx29(Spacer, {}),
    prefix && /* @__PURE__ */ jsx29("div", { style: {
      marginRight: prefixNoSpace ? 0 : 10
    }, children: prefix }),
    /* @__PURE__ */ jsx29(Content13, { children: children ? children : /* @__PURE__ */ jsxs20(Fragment4, { children: [
      /* @__PURE__ */ jsx29(Title5, { children: title }),
      description && /* @__PURE__ */ jsxs20(Fragment4, { children: [
        /* @__PURE__ */ jsx29(Spacer, { height: 3 }),
        /* @__PURE__ */ jsx29(Description5, { children: description })
      ] })
    ] }) }),
    suffix && /* @__PURE__ */ jsx29("div", { style: {
      marginLeft: suffixNoSpace ? 0 : 10
    }, children: suffix })
  ] });
};

// src/entity/footer.tsx
import { styled as styled25 } from "styled-components";
import { jsx as jsx30 } from "react/jsx-runtime";
var Footer2 = styled25("footer").withConfig({
  componentId: "sc-1dsloc6-0"
})`padding:10px 20px;display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;font-size:16px;font-weight:600;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var EntityFooter = ({
  children
}) => {
  return /* @__PURE__ */ jsx30(Footer2, { children });
};

// src/entity/header.tsx
import { styled as styled26 } from "styled-components";
import { jsx as jsx31 } from "react/jsx-runtime";
var Header3 = styled26("header").withConfig({
  componentId: "sc-sr61ae-0"
})`padding:10px 20px;display:flex;flex-direction:row;flex-wrap:wrap;flex:1;justify-content:flex-start;align-items:stretch;font-size:16px;font-weight:600;&:not(:first-child){border-top:solid 1px ${theme.colors.gray300};}`;
var EntityHeader = ({
  children
}) => {
  return /* @__PURE__ */ jsx31(Header3, { children });
};

// src/entity/wrapper.tsx
import { styled as styled27 } from "styled-components";
import { jsx as jsx32 } from "react/jsx-runtime";
var Wrapper12 = styled27("div").withConfig({
  componentId: "sc-uenel6-0"
})`border-radius:12px;border:1px solid ${theme.colors.gray300};display:flex;flex-direction:column;position:relative;color:${theme.colors.foreground};`;
var EntityWrapper = ({
  children,
  style,
  skeleton
}) => {
  return /* @__PURE__ */ jsx32(Wrapper12, { style, children });
};

// src/entity/index.ts
var Entity = {
  Wrapper: EntityWrapper,
  Footer: EntityFooter,
  Field: EntityField,
  Content: EntityContent,
  Header: EntityHeader,
  ContentRaw: EntityContentRaw
};

// src/flex/index.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var Flex = ({
  children,
  direction = "row",
  align,
  justify,
  wrap = "nowrap",
  gap = 0,
  ...props
}) => {
  return /* @__PURE__ */ jsx33("div", { ...props, style: {
    display: "flex",
    flexDirection: direction,
    alignItems: align,
    justifyContent: justify,
    flexWrap: wrap,
    gap,
    ...props.style
  }, children });
};

// src/group/index.tsx
import React7, { useContext as useContext2 } from "react";
import { styled as styled28 } from "styled-components";
import { jsx as jsx34, jsxs as jsxs21 } from "react/jsx-runtime";
var GroupWrapper = styled28("div").withConfig({
  componentId: "sc-gozipf-0"
})`border:solid 1px ${theme.colors.gray300};border-radius:10px;overflow:hidden;& > *:not(:last-child){border-bottom:solid 1px ${theme.colors.gray300};}`;
var IsGroupContext = React7.createContext(false);
var useIsGroup = () => useContext2(IsGroupContext);
var Group = {
  Wrapper: (props) => /* @__PURE__ */ jsx34(IsGroupContext.Provider, { value: true, children: /* @__PURE__ */ jsx34(GroupWrapper, { children: props.children }) }),
  Header: (props) => /* @__PURE__ */ jsx34(Group.HeaderRow, { children: /* @__PURE__ */ jsxs21("div", { style: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6
  }, children: [
    /* @__PURE__ */ jsxs21("div", { style: {
      display: "flex",
      flexDirection: "column",
      gap: 4
    }, children: [
      /* @__PURE__ */ jsx34(Title3, { as: "h2", size: "3", weight: "strong", children: props.title }),
      props.description && /* @__PURE__ */ jsx34(Text, { size: "2", weight: "medium", color: "gray600", children: props.description })
    ] }),
    /* @__PURE__ */ jsx34("div", { style: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 10
    }, children: props.actions })
  ] }) }),
  HeaderRow: styled28("header").withConfig({
    componentId: "sc-gozipf-1"
  })`padding:10px 15px;display:flex;flex-direction:column;&:not(:last-child){border-bottom:solid 1px ${theme.colors.gray300};}`,
  Row: styled28("main").withConfig({
    componentId: "sc-gozipf-2"
  })`display:flex;flex-direction:column;`,
  Content: styled28("div").withConfig({
    componentId: "sc-gozipf-3"
  })`padding:15px;display:flex;flex-direction:column;`,
  Footer: styled28("footer").withConfig({
    componentId: "sc-gozipf-4"
  })`padding:10px 15px;display:flex;align-items:center;`
};

// src/info/index.tsx
import { RiInformation2Line } from "@remixicon/react";
import { styled as styled29 } from "styled-components";
import { jsx as jsx35 } from "react/jsx-runtime";
var Button2 = styled29("button").withConfig({
  componentId: "sc-6m25j9-0"
})`background:none;border:none;padding:3px;display:flex;align-items:center;justify-content:center;color:${theme.colors.gray600};& > div{border-radius:5px;display:flex;align-items:center;justify-content:center;svg{height:70%;width:70%;}}&:hover,&:focus{& > div{background:${theme.colors.gray300};color:${theme.colors.foreground};}}`;
var InfoTooltip = (props) => {
  return /* @__PURE__ */ jsx35(Tooltip, { content: props.children, children: /* @__PURE__ */ jsx35(Button2, { children: /* @__PURE__ */ jsx35("div", { style: {
    height: props.size ?? 20,
    width: props.size ?? 20
  }, children: /* @__PURE__ */ jsx35(RiInformation2Line, {}) }) }) });
};

// src/inlineCopy/index.tsx
import { RiCheckLine as RiCheckLine2, RiFileCopy2Line } from "@remixicon/react";
import copy2 from "copy-to-clipboard";
import { useRef as useRef6, useState as useState14 } from "react";
import { toast as toast2 } from "sonner";
import { styled as styled30 } from "styled-components";
import { jsx as jsx36 } from "react/jsx-runtime";
var Button3 = styled30("button").withConfig({
  componentId: "sc-udgk38-0"
})`background:none;border:none;padding:3px;display:flex;align-items:center;justify-content:center;color:${theme.colors.gray600};& > div{border-radius:5px;display:flex;align-items:center;justify-content:center;svg{height:70%;width:70%;}}&:hover,&:focus{& > div{background:${theme.colors.gray300};color:${theme.colors.foreground};}}`;
var InlineCopy = (props) => {
  let [copied, setCopied] = useState14(false);
  let copiedToRef = useRef6(null);
  if (!props.value) return null;
  return /* @__PURE__ */ jsx36(Tooltip, { content: props.tooltip ?? "Copy", children: /* @__PURE__ */ jsx36(Button3, { onClick: () => {
    copy2(props.value);
    toast2.success("Copied to clipboard");
    clearTimeout(copiedToRef.current);
    copiedToRef.current = setTimeout(() => {
      setCopied(false);
    }, 2e3);
  }, children: /* @__PURE__ */ jsx36("div", { style: {
    height: props.size ?? 20,
    width: props.size ?? 20
  }, children: copied ? /* @__PURE__ */ jsx36(RiCheckLine2, {}) : /* @__PURE__ */ jsx36(RiFileCopy2Line, {}) }) }) });
};

// src/tabs/index.tsx
import { useState as useState15 } from "react";
import { useWindowSize } from "react-use";
import { styled as styled31 } from "styled-components";
import { Fragment as Fragment5, jsx as jsx37, jsxs as jsxs22 } from "react/jsx-runtime";
var Wrapper13 = styled31("div").withConfig({
  componentId: "sc-1b4u48t-0"
})`position:relative;width:100%;`;
var Inner4 = styled31("div").withConfig({
  componentId: "sc-1b4u48t-1"
})`display:flex;padding:0;margin:0;border-bottom:1px solid ${theme.colors.gray300};overflow-x:auto;white-space:nowrap;justify-content:center;`;
var List2 = styled31("ul").withConfig({
  componentId: "sc-1b4u48t-2"
})`list-style:none;display:flex;max-width:100%;overflow-x:auto;white-space:nowrap;`;
var Tab = styled31("li").withConfig({
  componentId: "sc-1b4u48t-3"
})`position:relative;z-index:10;button,a{font-size:14px;font-weight:500;color:${theme.colors.gray700};text-decoration:none;display:flex;align-items:center;justify-content:center;transition:all 0.3s ease;background:none;border:none;padding:0;&:hover{color:${theme.colors.blue900};}}`;
var LineIndicator = styled31("div").withConfig({
  componentId: "sc-1b4u48t-4"
})`position:absolute;bottom:-1px;height:3px;background:${theme.colors.blue900};transition:all 0.3s ease;border-radius:7px;`;
var TabIndicator = styled31("div").withConfig({
  componentId: "sc-1b4u48t-5"
})`position:absolute;top:0;transition:all 0.3s ease;border-radius:7px;z-index:0;`;
var Tabs = ({
  current,
  tabs,
  action,
  tabIndicator = true,
  lineIndicator = true,
  height = 30,
  gap = 20,
  padding: paddingRaw = {},
  margin = {
    bottom: 20,
    top: 0
  },
  variant = "soft",
  maxWidth = "100%"
}) => {
  let Link = getLink();
  useWindowSize();
  let padding = {
    left: paddingRaw.left ?? 0,
    right: paddingRaw.right ?? 0,
    top: paddingRaw.top ?? 0,
    bottom: paddingRaw.bottom ?? 6
  };
  let [listRef, setListRef] = useState15(null);
  let [innerRef, setInnerRef] = useState15(null);
  let [refs, setRefs] = useState15([]);
  let renderIndicators = false;
  let listOffset = Math.max(((innerRef?.clientWidth ?? 0) - (listRef?.clientWidth ?? 0)) / 2, 0);
  let currentIndex = tabs.findIndex((t) => "to" in t ? t.to == current : t.id == current);
  let indicatorOffset = 0;
  let indicatorWidth = refs[currentIndex]?.clientWidth ?? 0;
  if (refs[currentIndex]) {
    renderIndicators = true;
    indicatorOffset = refs.reduce((acc, ref, i) => {
      if (i < currentIndex && ref) return acc + ref.clientWidth;
      return acc;
    }, 0);
  }
  return /* @__PURE__ */ jsxs22(Fragment5, { children: [
    !!margin.top && /* @__PURE__ */ jsx37(Spacer, { height: margin.top }),
    /* @__PURE__ */ jsxs22(Wrapper13, { children: [
      /* @__PURE__ */ jsx37(Inner4, { ref: setInnerRef, style: {
        width: "100%",
        paddingLeft: padding.left,
        paddingRight: padding.right,
        paddingTop: padding.top,
        paddingBottom: padding.bottom
        // marginBottom: margin.bottom,
        // marginTop: margin.top
      }, children: /* @__PURE__ */ jsx37(List2, { ref: setListRef, style: {
        width: maxWidth,
        gap
      }, children: tabs.map((item, i) => /* @__PURE__ */ jsx37(Tab, { ref: (el) => {
        setRefs((refs2) => {
          refs2[i] = el;
          return refs2;
        });
      }, children: "to" in item ? /* @__PURE__ */ jsx37(Link, { to: item.to, onClick: () => action(item.to), style: {
        height
      }, children: item.label }) : /* @__PURE__ */ jsx37("button", { onClick: () => action(item.id), style: {
        height
      }, children: item.label }) }, i)) }) }),
      renderIndicators && /* @__PURE__ */ jsxs22(Fragment5, { children: [
        lineIndicator && /* @__PURE__ */ jsx37(LineIndicator, { style: {
          left: indicatorOffset + currentIndex * gap + listOffset,
          width: indicatorWidth
        } }),
        tabIndicator && /* @__PURE__ */ jsx37(TabIndicator, { style: {
          left: indicatorOffset + (currentIndex - 0.5) * gap + listOffset,
          width: indicatorWidth + gap,
          height,
          ...variant === "soft" && {
            background: theme.colors.gray300
          },
          ...variant === "outline" && {
            border: `1px solid ${theme.colors.gray400}`
          }
        } })
      ] })
    ] }),
    !!margin.bottom && /* @__PURE__ */ jsx37(Spacer, { height: margin.bottom })
  ] });
};

// src/linkTabs/index.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var LinkTabs = ({
  current,
  links,
  tabIndicator,
  lineIndicator,
  height,
  gap,
  padding,
  variant,
  margin,
  maxWidth
}) => {
  return /* @__PURE__ */ jsx38(Tabs, { current, tabs: links, action: () => {
  }, tabIndicator, lineIndicator, height, gap, margin, padding, variant, maxWidth });
};

// src/logo.tsx
import { jsx as jsx39, jsxs as jsxs23 } from "react/jsx-runtime";
var LoopboundLogo = ({
  size = 30
}) => {
  return /* @__PURE__ */ jsxs23("svg", { width: size, height: size, viewBox: "0 0 60 60", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
    /* @__PURE__ */ jsxs23("g", { clipPath: "url(#clip0_601_2)", children: [
      /* @__PURE__ */ jsx39("path", { d: "M25 0H0V25H25V0Z", fill: "#000000" }),
      /* @__PURE__ */ jsx39("path", { d: "M47.5 25C54.4036 25 60 19.4036 60 12.5C60 5.59644 54.4036 0 47.5 0C40.5964 0 35 5.59644 35 12.5C35 19.4036 40.5964 25 47.5 25Z", fill: "#000000" }),
      /* @__PURE__ */ jsx39("path", { d: "M60 35H35V60H60V35Z", fill: "#000000" }),
      /* @__PURE__ */ jsx39("path", { d: "M12.5 60C19.4036 60 25 54.4036 25 47.5C25 40.5964 19.4036 35 12.5 35C5.59644 35 0 40.5964 0 47.5C0 54.4036 5.59644 60 12.5 60Z", fill: "#000000" })
    ] }),
    /* @__PURE__ */ jsx39("defs", { children: /* @__PURE__ */ jsx39("clipPath", { id: "clip0_601_2", children: /* @__PURE__ */ jsx39("rect", { width: "60", height: "60", fill: "white" }) }) })
  ] });
};
var CenteredLoopboundLogo = ({
  size
}) => {
  return /* @__PURE__ */ jsx39("div", { style: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, children: /* @__PURE__ */ jsx39(LoopboundLogo, { size }) });
};

// src/menu/index.tsx
import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import { useEffect as useEffect9, useLayoutEffect as useLayoutEffect2, useState as useState16 } from "react";
import { keyframes as keyframes8, styled as styled32 } from "styled-components";
import { Fragment as Fragment6, jsx as jsx40, jsxs as jsxs24 } from "react/jsx-runtime";
var fadeInBottom2 = keyframes8`
  from { opacity: 0; transform: translateY(-10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutBottom2 = keyframes8`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
`;
var fadeInTop2 = keyframes8`
  from { opacity: 0; transform: translateY(10px) scale(0.99); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
var fadeOutTop2 = keyframes8`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(10px); }
`;
var fadeInLeft2 = keyframes8`
  from { opacity: 0; transform: translateX(-10px) scale(0.99); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;
var fadeOutLeft2 = keyframes8`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-10px); }
`;
var fadeInRight2 = keyframes8`
  from { opacity: 0; transform: translateX(10px) scale(0.99); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;
var fadeOutRight2 = keyframes8`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(10px); }
`;
var Content15 = styled32(RadixMenu.Content).withConfig({
  componentId: "sc-14qtlbi-0"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;color:${theme.colors.background};box-shadow:${theme.shadows.large};background:${theme.colors.foreground};border-radius:10px;min-width:200px;gap:5px;&[data-state='open'][data-side='top']{animation:${fadeInTop2} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutTop2} 0.2s ease forwards;}&[data-state='open'][data-side='bottom']{animation:${fadeInBottom2} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutBottom2} 0.2s ease forwards;}&[data-state='open'][data-side='left']{animation:${fadeInLeft2} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft2} 0.2s ease forwards;}&[data-state='open'][data-side='right']{animation:${fadeInRight2} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight2} 0.2s ease forwards;}`;
var Separator2 = styled32(RadixMenu.Separator).withConfig({
  componentId: "sc-14qtlbi-1"
})`height:1px;background:${theme.colors.gray800};`;
var Item6 = styled32(RadixMenu.Item).withConfig({
  componentId: "sc-14qtlbi-2"
})`font-size:14px;font-weight:500;display:flex;align-items:center;gap:5px;padding:7px 13px;border-radius:6px;outline:none;border:none;background:transparent;transition:all 0.3s ease;color:${theme.colors.background};&:not(:disabled){&:hover{background:${theme.colors.gray800};}&:focus{background:${theme.colors.gray800};}}&:disabled{opacity:0.7;}`;
var Title6 = styled32(RadixMenu.Label).withConfig({
  componentId: "sc-14qtlbi-3"
})`font-size:14px;font-weight:600;color:${theme.colors.gray100};padding:7px 13px;border-radius:6px;background:transparent;transition:all 0.3s ease;display:flex;align-items:center;gap:5px;`;
var Menu = ({
  children,
  label,
  onItemClick,
  items,
  title,
  setIsOpen
}) => {
  let [open, setOpen] = useState16(false);
  let [content, setContent] = useState16(null);
  let zIndex = useDialogZIndex(open);
  useLayoutEffect2(() => {
    let parent = content?.parentElement;
    if (parent) parent.style.zIndex = zIndex.toString();
  }, [content, zIndex]);
  useEffect9(() => setIsOpen?.(open), [open]);
  return /* @__PURE__ */ jsxs24(RadixMenu.Root, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx40(RadixMenu.Trigger, { "aria-label": label, asChild: true, children }),
    /* @__PURE__ */ jsx40(RadixMenu.Portal, { children: /* @__PURE__ */ jsxs24(Content15, { sideOffset: 5, ref: setContent, children: [
      title && /* @__PURE__ */ jsxs24(Fragment6, { children: [
        /* @__PURE__ */ jsx40(Title6, { children: title }),
        /* @__PURE__ */ jsx40(Separator2, {})
      ] }),
      items.map((item, i) => item.type === "separator" ? /* @__PURE__ */ jsx40(Separator2, {}, i) : /* @__PURE__ */ jsx40(Item6, { onClick: () => onItemClick?.(item.id), disabled: item.disabled, asChild: true, children: /* @__PURE__ */ jsx40("button", { disabled: item.disabled, children: item.label }) }, i))
    ] }) })
  ] });
};

// src/multiSelect/index.tsx
import * as RadixPopover2 from "@radix-ui/react-popover";
import { VisuallyHidden as VisuallyHidden4 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine2 } from "@remixicon/react";
import { useId as useId3 } from "react";
import { useMeasure as useMeasure2 } from "react-use";
import { keyframes as keyframes9, styled as styled33 } from "styled-components";
import { jsx as jsx41, jsxs as jsxs25 } from "react/jsx-runtime";
var fadeIn5 = keyframes9`
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Trigger8 = styled33(RadixPopover2.Trigger).withConfig({
  componentId: "sc-109j6zo-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray200};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:all 0.3s ease;border:none;font-weight:500;flex-shrink:0;&:focus-within,&:focus{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}&[data-placeholder]{color:${theme.colors.gray700};}`;
var Content17 = styled33(RadixPopover2.Content).withConfig({
  componentId: "sc-109j6zo-1"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:15px;box-shadow:${theme.shadows.medium};background:${theme.colors.background};font-size:14px;font-weight:500;border-radius:8px;gap:10px;width:100%;&[data-state='open']{animation:${fadeIn5} 0.15s ease forwards;}`;
var Item7 = styled33("div").withConfig({
  componentId: "sc-109j6zo-2"
})``;
var Value2 = styled33("div").withConfig({
  componentId: "sc-109j6zo-3"
})`font-size:12px;color:${theme.colors.background};background:${theme.colors.foreground};padding:3px 5px;border-radius:5px;box-shadow:${theme.shadows.small};`;
var TriggerValues = styled33("div").withConfig({
  componentId: "sc-109j6zo-4"
})`display:flex;gap:5px;flex-wrap:wrap;`;
var MultiSelect = ({
  size = "3",
  disabled,
  value,
  onChange,
  label,
  hideLabel,
  description,
  placeholder,
  error,
  items
}) => {
  let id = useId3();
  let sizeStyles = getButtonSize(size);
  let [ref, {
    width
  }] = useMeasure2();
  return /* @__PURE__ */ jsxs25("div", { style: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0
  }, children: [
    label && (hideLabel ? /* @__PURE__ */ jsx41(VisuallyHidden4, { children: /* @__PURE__ */ jsx41("label", { htmlFor: id, children: label }) }) : /* @__PURE__ */ jsx41(InputLabel, { htmlFor: id, children: label })),
    description && /* @__PURE__ */ jsx41(InputDescription, { children: description }),
    /* @__PURE__ */ jsxs25(RadixPopover2.Root, { children: [
      /* @__PURE__ */ jsxs25(Trigger8, { id, disabled, "aria-label": label, style: {
        ...sizeStyles,
        fontSize: 14
      }, ref, children: [
        /* @__PURE__ */ jsx41(TriggerValues, { children: [...value ?? []].sort().map((v) => /* @__PURE__ */ jsx41(Value2, { children: items.find((i) => i.id === v)?.label })) }),
        /* @__PURE__ */ jsx41(RiArrowDownSLine2, { size: 14, style: {
          opacity: 0.5
        } })
      ] }),
      /* @__PURE__ */ jsx41(RadixPopover2.Portal, { children: /* @__PURE__ */ jsx41(Content17, { sideOffset: 5, style: {
        width: width + 20,
        zIndex: 9999
      }, children: items.map((item, i) => /* @__PURE__ */ jsx41(Item7, { children: /* @__PURE__ */ jsx41(Checkbox, { checked: value?.includes(item.id), onCheckedChange: (checked) => {
        if (checked) {
          onChange?.([...value, item.id]);
        } else {
          onChange?.((value || [])?.filter((v) => v !== item.id));
        }
      }, label: item.label, disabled: item.disabled || disabled }) })) }) })
    ] }),
    error && /* @__PURE__ */ jsx41(Error, { children: error })
  ] });
};

// src/or/index.tsx
import { memo as memo6 } from "react";
import { styled as styled34 } from "styled-components";
import { jsx as jsx42, jsxs as jsxs26 } from "react/jsx-runtime";
var Wrapper14 = styled34("div").withConfig({
  componentId: "sc-1nbz00y-0"
})`display:grid;grid-template-columns:1fr min-content 1fr;gap:10px;width:100%;`;
var LineWrapper = styled34("div").withConfig({
  componentId: "sc-1nbz00y-1"
})`display:flex;align-items:center;gap:10px;`;
var Line = styled34("div").withConfig({
  componentId: "sc-1nbz00y-2"
})`height:1px;background-color:${theme.colors.gray400};flex-grow:1;`;
var Text2 = styled34("p").withConfig({
  componentId: "sc-1nbz00y-3"
})`color:${theme.colors.gray600};font-size:12px;font-weight:500;text-transform:uppercase;`;
var Or = memo6(() => {
  return /* @__PURE__ */ jsxs26(Wrapper14, { children: [
    /* @__PURE__ */ jsx42(LineWrapper, { children: /* @__PURE__ */ jsx42(Line, {}) }),
    /* @__PURE__ */ jsx42(Text2, { children: "or" }),
    /* @__PURE__ */ jsx42(LineWrapper, { children: /* @__PURE__ */ jsx42(Line, {}) })
  ] });
});

// src/popover/index.tsx
import * as RadixPopover3 from "@radix-ui/react-popover";
import { useEffect as useEffect10, useState as useState17 } from "react";
import { keyframes as keyframes10, styled as styled35 } from "styled-components";
import { jsx as jsx43, jsxs as jsxs27 } from "react/jsx-runtime";
var fadeInTop3 = keyframes10`
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var fadeOutTop3 = keyframes10`
  from {
    opacity: 1;
    transform: translateY(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateY(-10px) scale(1);
  }
`;
var fadeInBottom3 = keyframes10`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var fadeOutBottom3 = keyframes10`
  from {
    opacity: 1;
    transform: translateY(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateY(10px) scale(1);
  }
`;
var fadeInLeft3 = keyframes10`
  from {
    opacity: 0;
    transform: translateX(-10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;
var fadeOutLeft3 = keyframes10`
  from {
    opacity: 1;
    transform: translateX(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateX(-10px) scale(1);
  }
`;
var fadeInRight3 = keyframes10`
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;
var fadeOutRight3 = keyframes10`
  from {
    opacity: 1;
    transform: translateX(0) scale(0.99);
  }

  to {
    opacity: 0;
    transform: translateX(10px) scale(1);
  }
`;
var Wrapper15 = styled35(RadixPopover3.Content).withConfig({
  componentId: "sc-1xsnhtn-0"
})`display:flex;gap:10;align-items:center;cursor:pointer;box-shadow:${theme.shadows.medium};background:${theme.colors.background};border-radius:10px;min-width:200px;will-change:transform,opacity;outline:none;&[data-state='open'][data-side='top']{animation:${fadeInBottom3} 0.2s ease forwards;}&[data-state='closed'][data-side='top']{animation:${fadeOutBottom3} 0.2s ease forwards;}&[data-state='open'][data-side='bottom']{animation:${fadeInTop3} 0.2s ease forwards;}&[data-state='closed'][data-side='bottom']{animation:${fadeOutTop3} 0.2s ease forwards;}&[data-state='open'][data-side='left']{animation:${fadeInLeft3} 0.2s ease forwards;}&[data-state='closed'][data-side='left']{animation:${fadeOutLeft3} 0.2s ease forwards;}&[data-state='open'][data-side='right']{animation:${fadeInRight3} 0.2s ease forwards;}&[data-state='closed'][data-side='right']{animation:${fadeOutRight3} 0.2s ease forwards;}`;
var ContentWrapper = styled35("div").withConfig({
  componentId: "sc-1xsnhtn-1"
})`padding:15px;`;
var Arrow4 = styled35(RadixPopover3.Arrow).withConfig({
  componentId: "sc-1xsnhtn-2"
})`fill:${theme.colors.background};`;
var Root15 = ({
  trigger,
  children,
  arrow,
  operationKey,
  onOpenChange,
  sideOffset,
  alignOffset,
  open: openProp
}) => {
  let [open, setOpen] = useState17(false);
  useEffect10(() => {
    setOpen(false);
  }, [operationKey]);
  useEffect10(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open]);
  useEffect10(() => {
    if (typeof openProp === "boolean") setOpen(openProp);
  }, [openProp]);
  let zIndex = useDialogZIndex(open);
  return /* @__PURE__ */ jsxs27(RadixPopover3.Root, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx43(RadixPopover3.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx43(RadixPopover3.Portal, { children: /* @__PURE__ */ jsxs27(Wrapper15, { style: {
      zIndex
    }, sideOffset, alignOffset, children: [
      children,
      arrow && /* @__PURE__ */ jsx43(Arrow4, {})
    ] }) })
  ] });
};
var Content19 = ({
  children
}) => {
  return /* @__PURE__ */ jsx43(ContentWrapper, { children });
};
var Popover = Object.assign(Root15, {
  Content: Content19,
  Root: Root15
});

// src/select/index.tsx
import * as RadixSelect from "@radix-ui/react-select";
import { VisuallyHidden as VisuallyHidden5 } from "@radix-ui/react-visually-hidden";
import { RiArrowDownSLine as RiArrowDownSLine3, RiArrowUpSLine, RiCheckLine as RiCheckLine3 } from "@remixicon/react";
import { useEffect as useEffect11, useId as useId4, useMemo as useMemo5 } from "react";
import { keyframes as keyframes11, styled as styled36 } from "styled-components";
import { jsx as jsx44, jsxs as jsxs28 } from "react/jsx-runtime";
var fadeIn6 = keyframes11`
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;
var Trigger11 = styled36(RadixSelect.Trigger).withConfig({
  componentId: "sc-gtvbj9-0"
})`display:flex;outline:1px solid transparent;background:${theme.colors.gray200};color:${theme.colors.foreground};outline:none;width:100%;gap:10px;align-items:center;justify-content:space-between;transition:all 0.3s ease;border:none;font-weight:500;flex-shrink:0;&:focus-within,&:focus{background:${theme.colors.gray300};outline:1px solid ${theme.colors.gray600};}&[data-placeholder]{color:${theme.colors.gray700};}`;
var Content21 = styled36(RadixSelect.Content).withConfig({
  componentId: "sc-gtvbj9-1"
})`display:flex;flex-direction:column;transition:all 0.3s ease;padding:5px;box-shadow:${theme.shadows.medium};background:${theme.colors.gray100};font-size:14px;font-weight:500;border-radius:8px;gap:5px;&[data-state='open']{animation:${fadeIn6} 0.15s ease forwards;}`;
var Separator4 = styled36(RadixSelect.Separator).withConfig({
  componentId: "sc-gtvbj9-2"
})`height:1;background:${theme.colors.gray300};margin:5px 0;`;
var Item9 = styled36(RadixSelect.Item).withConfig({
  componentId: "sc-gtvbj9-3"
})`font-size:14;font-weight:500;display:flex;align-items:center;gap:5px;padding:5px 10px 5px 5px;border-radius:6;outline:none;cursor:pointer;height:30px;border-radius:5px;&:hover{background:${theme.colors.gray300};}&:focus{background:${theme.colors.gray300};}&[data-state='checked']{background:${theme.colors.gray400};}&:disabled,&[data-disabled]{opacity:0.5;cursor:not-allowed;}`;
var Indicator4 = styled36(RadixSelect.ItemIndicator).withConfig({
  componentId: "sc-gtvbj9-4"
})`display:flex;align-items:center;justify-content:center;width:20px;height:20px;opacity:0.5;`;
var Select = ({
  size = "3",
  disabled,
  value,
  onChange,
  label,
  hideLabel,
  description,
  placeholder,
  error,
  items
}) => {
  let id = useId4();
  let sizeStyles = getButtonSize(size);
  let disabledItems = useMemo5(() => items.filter((item) => item.disabled).map((item) => item.id).sort().join(","), [items]);
  useEffect11(() => {
    let disabledSelected = items.find((item) => item.id === value && item.disabled);
    if (disabledSelected) {
      let notDisabled = items.find((item) => !item.disabled);
      if (notDisabled) onChange?.(notDisabled.id);
    }
  }, [value, disabledItems]);
  return /* @__PURE__ */ jsxs28("div", { style: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    textAlign: "initial"
  }, children: [
    label && (hideLabel ? /* @__PURE__ */ jsx44(VisuallyHidden5, { children: /* @__PURE__ */ jsx44("label", { htmlFor: id, children: label }) }) : /* @__PURE__ */ jsx44(InputLabel, { htmlFor: id, children: label })),
    description && /* @__PURE__ */ jsx44(InputDescription, { children: description }),
    /* @__PURE__ */ jsxs28(RadixSelect.Root, { value, onValueChange: onChange, children: [
      /* @__PURE__ */ jsxs28(Trigger11, { id, disabled, "aria-label": label, style: {
        ...sizeStyles,
        fontSize: 14
      }, children: [
        /* @__PURE__ */ jsx44(RadixSelect.Value, { placeholder }),
        /* @__PURE__ */ jsx44(RadixSelect.Icon, { children: /* @__PURE__ */ jsx44(RiArrowDownSLine3, { size: 14, style: {
          opacity: 0.5
        } }) })
      ] }),
      /* @__PURE__ */ jsx44(RadixSelect.Portal, { children: /* @__PURE__ */ jsxs28(Content21, { style: {
        zIndex: 9999
      }, children: [
        /* @__PURE__ */ jsx44(RadixSelect.ScrollUpButton, { className: "SelectScrollButton", children: /* @__PURE__ */ jsx44(RiArrowUpSLine, { size: 14 }) }),
        /* @__PURE__ */ jsx44(RadixSelect.Viewport, { className: "SelectViewport", children: items.map((item, i) => item.type == "separator" ? /* @__PURE__ */ jsx44(Separator4, {}, i) : /* @__PURE__ */ jsxs28(Item9, { value: item.id, disabled: item.disabled, children: [
          /* @__PURE__ */ jsx44("div", { style: {
            width: 20
          }, children: /* @__PURE__ */ jsx44(Indicator4, { children: /* @__PURE__ */ jsx44(RiCheckLine3, { size: 12, strokeWidth: 3.5 }) }) }),
          /* @__PURE__ */ jsx44(RadixSelect.ItemText, { children: item.label })
        ] }, i)) }),
        /* @__PURE__ */ jsx44(RadixSelect.ScrollDownButton, { className: "SelectScrollButton", children: /* @__PURE__ */ jsx44(RiArrowDownSLine3, { size: 14 }) })
      ] }) })
    ] }),
    error && /* @__PURE__ */ jsx44(Error, { children: error })
  ] });
};

// src/showModal/confirm.tsx
import { jsx as jsx45, jsxs as jsxs29 } from "react/jsx-runtime";
var confirm = ({
  title,
  description,
  onConfirm,
  onCancel,
  cancelText = "Cancel",
  confirmText = "Confirm"
}) => {
  showModal(({
    dialogProps
  }) => {
    return /* @__PURE__ */ jsxs29(Alert.Wrapper, { ...dialogProps, children: [
      /* @__PURE__ */ jsx45(Alert.Title, { children: title }),
      /* @__PURE__ */ jsx45(Alert.Description, { children: description }),
      /* @__PURE__ */ jsxs29(Alert.Actions, { children: [
        /* @__PURE__ */ jsx45(Alert.Action, { type: "cancel", children: /* @__PURE__ */ jsx45(Button, { variant: "soft", onClick: onCancel, children: cancelText }) }),
        /* @__PURE__ */ jsx45(Alert.Action, { type: "action", children: /* @__PURE__ */ jsx45(Button, { variant: "solid", onClick: onConfirm, children: confirmText }) })
      ] })
    ] });
  });
};

// src/showModal/showModal.tsx
import { useEffect as useEffect12, useRef as useRef7, useState as useState18 } from "react";
import { Fragment as Fragment7, jsx as jsx46 } from "react/jsx-runtime";
var modalAtom = atom([]);
var currentId = 0;
var showModal = (Body, opts = {}) => {
  let id = currentId++;
  let setOpenRef = {
    current: null
  };
  let hide = () => {
    setOpenRef = {
      current: null
    };
    setTimeout(() => {
      modalAtom.set((modals) => modals.filter((m) => m.id != id));
    }, 500);
  };
  let Component = () => {
    let [open, setOpen] = useState18(false);
    setOpenRef.current = setOpen;
    let onCloseInternal = () => {
      setOpen(false);
      hide();
      opts.onClose?.();
    };
    useEffect12(() => () => {
      setOpenRef.current = null;
    }, []);
    useEffect12(() => {
      setTimeout(() => setOpen(true), 2);
    }, []);
    let onOpenChange = (isOpen) => {
      setOpen(isOpen);
      if (!isOpen) onCloseInternal();
    };
    return /* @__PURE__ */ jsx46(Body, { isOpen: open, dialogProps: {
      isOpen: open,
      onOpenChange
    }, close: onCloseInternal });
  };
  modalAtom.set((modals) => [...modals, {
    id,
    component: Component
  }]);
  return {
    close: () => {
      if (setOpenRef.current) setOpenRef.current(false);
      hide();
    }
  };
};
var ModalRoot = () => {
  let modals = useAtom(modalAtom);
  let pathname = usePathname();
  useEffect12(() => {
    modalAtom.set([]);
  }, [pathname]);
  return /* @__PURE__ */ jsx46(Fragment7, { children: modals.map((m) => /* @__PURE__ */ jsx46(m.component, {}, m.id)) });
};
var usePathname = () => {
  let [pathname, setPathname] = useState18(() => window.location.pathname);
  let currentPathRef = useRef7(pathname);
  currentPathRef.current = pathname;
  useEffect12(() => {
    let handler = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);
  useEffect12(() => {
    let to = setInterval(() => {
      if (currentPathRef.current != window.location.pathname) {
        setPathname(window.location.pathname);
      }
    }, 500);
    return () => clearInterval(to);
  }, []);
  return pathname;
};

// src/sortableCheckList/index.tsx
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RiDraggable } from "@remixicon/react";
import { useEffect as useEffect13, useState as useState19 } from "react";
import { styled as styled37 } from "styled-components";
import { jsx as jsx47, jsxs as jsxs30 } from "react/jsx-runtime";
var List3 = styled37("ul").withConfig({
  componentId: "sc-ov2onc-0"
})`list-style:none;padding:0;margin:0;display:flex;flex-direction:column;`;
var ListItem = styled37("li").withConfig({
  componentId: "sc-ov2onc-1"
})`padding:5px 0px;display:flex;gap:10px;align-items:center;width:100%;`;
var SortableCheckList = ({
  items,
  onChange,
  syncItems,
  sortable = true
}) => {
  let [innerItems, setInnerItems] = useState19(() => items);
  let sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  let setItems = (items2) => {
    setInnerItems(items2);
    onChange(items2);
  };
  useEffect13(() => {
    if (syncItems && items != innerItems) {
      setInnerItems(items);
    } else if (!syncItems) {
      let newIDs = new Set(items.map((i) => i.id));
      let currentIDs = new Set(innerItems.map((i) => i.id));
      if (newIDs.size !== currentIDs.size || [...newIDs].some((id) => !currentIDs.has(id))) {
        setInnerItems(items);
      }
    }
  }, [items, syncItems]);
  return /* @__PURE__ */ jsx47(DndContext, { sensors, collisionDetection: closestCenter, onDragEnd: (event) => {
    let {
      active,
      over
    } = event;
    if (active && over && active.id !== over.id) {
      let oldIndex = innerItems.findIndex((item) => item.id === active.id);
      let newIndex = innerItems.findIndex((item) => item.id === over.id);
      setItems(arrayMove(innerItems, oldIndex, newIndex));
    }
  }, children: /* @__PURE__ */ jsx47(SortableContext, { items: innerItems, strategy: verticalListSortingStrategy, children: /* @__PURE__ */ jsx47(List3, { children: innerItems.map((item, i) => /* @__PURE__ */ jsx47(SortableItem, { id: item.id, item, sortable, onCheckedChange: () => {
    setItems(innerItems.map((i2) => {
      if (i2.id === item.id) {
        return {
          ...i2,
          isChecked: !i2.isChecked
        };
      }
      return i2;
    }));
  } }, `${item.id}-${i}`)) }) }) });
};
var SortableItem = ({
  id,
  item,
  sortable,
  onCheckedChange
}) => {
  let {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id
  });
  let style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return /* @__PURE__ */ jsxs30(ListItem, { "data-dnd-id": id, ref: setNodeRef, style, children: [
    sortable && /* @__PURE__ */ jsx47("div", { ...attributes, ...listeners, style: {
      display: "flex",
      alignItems: "center",
      cursor: "grab"
    }, children: /* @__PURE__ */ jsx47(RiDraggable, { size: 16, color: String(theme.colors.gray600) }) }),
    /* @__PURE__ */ jsx47(Checkbox, { label: item.label, checked: item.isChecked, onCheckedChange })
  ] });
};

// src/switch/index.tsx
import * as RadixSwitch from "@radix-ui/react-switch";
import { VisuallyHidden as VisuallyHidden6 } from "@radix-ui/react-visually-hidden";
import clsx2 from "clsx";
import { createRef, useId as useId5 } from "react";
import { styled as styled38 } from "styled-components";
import { Fragment as Fragment8, jsx as jsx48, jsxs as jsxs31 } from "react/jsx-runtime";
var Wrapper16 = styled38("div").withConfig({
  componentId: "sc-16dujq9-0"
})`display:flex;gap:10px;align-items:center;cursor:pointer;`;
var Root18 = styled38(RadixSwitch.Root).withConfig({
  componentId: "sc-16dujq9-1"
})`display:flex;height:20px;width:30px;padding:0;justify-content:center;align-items:center;border-radius:50px;background:${theme.colors.gray300};border:solid 1px ${theme.colors.gray300};position:relative;transition:all 0.3s ease;&:focus{border:solid 1px ${theme.colors.blue900};}&[data-state='checked']{background:${theme.colors.blue900};}&.disabled{opacity:0.5;}`;
var Thumb2 = styled38(RadixSwitch.Thumb).withConfig({
  componentId: "sc-16dujq9-2"
})`display:flex;height:14px;width:14px;border-radius:500;background:${theme.colors.background};box-shadow:none;position:absolute;transition:all 0.3s ease;border-radius:50px;top:2px;${Root18}[data-state='checked'] &{left:12px;}${Root18}[data-state='unchecked'] &{left:2px;}`;
var Switch = ({
  checked,
  onCheckedChange,
  label,
  disabled,
  description,
  hideLabel
}) => {
  let id = useId5();
  let root = createRef();
  return /* @__PURE__ */ jsx48(Fragment8, { children: /* @__PURE__ */ jsxs31(Wrapper16, { children: [
    /* @__PURE__ */ jsx48(Root18, { className: clsx2({
      disabled
    }), id, checked, onCheckedChange, ref: root, children: /* @__PURE__ */ jsx48(Thumb2, {}) }),
    /* @__PURE__ */ jsxs31("div", { onClick: (e) => root.current?.click(), children: [
      hideLabel ? /* @__PURE__ */ jsx48(VisuallyHidden6, { children: /* @__PURE__ */ jsx48("label", { htmlFor: id, children: label }) }) : /* @__PURE__ */ jsx48(InputLabel, { htmlFor: id, style: {
        margin: 0
      }, onClick: (e) => e.stopPropagation(), children: label }),
      description && /* @__PURE__ */ jsx48(InputDescription, { style: {
        marginTop: 0,
        marginBottom: 0
      }, children: description })
    ] })
  ] }) });
};

// src/textArrayInput/index.tsx
import { RiDeleteBin4Line } from "@remixicon/react";
import { useEffect as useEffect14 } from "react";
import { styled as styled39 } from "styled-components";
import { Fragment as Fragment9, jsx as jsx49, jsxs as jsxs32 } from "react/jsx-runtime";
var Wrapper17 = styled39("div").withConfig({
  componentId: "sc-163k28x-0"
})`display:flex;flex-direction:column;gap:10px;`;
var Item10 = styled39("div").withConfig({
  componentId: "sc-163k28x-1"
})`display:flex;flex-direction:column;gap:5px;`;
var ItemInner2 = styled39("div").withConfig({
  componentId: "sc-163k28x-2"
})`display:flex;gap:10px;align-items:flex-end;`;
var TextArrayInput = ({
  value,
  label,
  error,
  onChange,
  placeholder
}) => {
  useEffect14(() => {
    if (value.length === 0) onChange([""]);
  }, [value]);
  return /* @__PURE__ */ jsxs32(Fragment9, { children: [
    label && /* @__PURE__ */ jsxs32(Fragment9, { children: [
      /* @__PURE__ */ jsx49(InputLabel, { children: label }),
      /* @__PURE__ */ jsx49(Spacer, { height: 5 })
    ] }),
    /* @__PURE__ */ jsxs32(Wrapper17, { children: [
      value.map((v, i) => /* @__PURE__ */ jsxs32(Item10, { children: [
        /* @__PURE__ */ jsxs32(ItemInner2, { children: [
          /* @__PURE__ */ jsx49(Input, { value: v, placeholder, onChange: (e) => {
            let newValue = [...value];
            newValue[i] = e.target.value;
            onChange(newValue);
          }, label, style: {
            flexGrow: 1
          }, size: "2", hideLabel: true }),
          /* @__PURE__ */ jsx49(Button, { onClick: () => onChange(value.filter((_, j) => j != i)), title: "Remove", iconLeft: /* @__PURE__ */ jsx49(RiDeleteBin4Line, {}), size: "2", variant: "soft", disabled: value.length === 1 })
        ] }),
        Array.isArray(error) && error[i] && /* @__PURE__ */ jsx49(Error, { children: error[i] })
      ] }, i)),
      /* @__PURE__ */ jsx49("div", { style: {
        display: "flex",
        gap: 10
      }, children: /* @__PURE__ */ jsx49(Button, { size: "2", onClick: () => onChange([...value, ""]), children: "Add" }) })
    ] })
  ] });
};

// src/toaster/index.tsx
import { Toaster as BaseToaster } from "sonner";
import { jsx as jsx50 } from "react/jsx-runtime";
var Toaster = () => /* @__PURE__ */ jsx50(BaseToaster, { position: "bottom-right", richColors: true });
export {
  Accordion,
  AccordionSingle,
  Alert,
  AnimateContent,
  AnimateHeight,
  AnimatePanes,
  Attributes,
  Avatar,
  Badge,
  Button,
  Callout,
  CardLinks,
  CenteredLoopboundLogo,
  CenteredSpinner,
  Checkbox,
  Code,
  Control,
  Copy,
  Datalist,
  DatePicker,
  Dialog,
  Entity,
  Error,
  Flex,
  Group,
  InfoTooltip,
  InlineCopy,
  Input,
  InputDescription,
  InputLabel,
  Kbd,
  LargePanelDialog,
  LinkButton,
  LinkTabs,
  LoopboundLogo,
  Menu,
  ModalRoot,
  MultiSelect,
  Or,
  Popover,
  RenderDate,
  Select,
  SortableCheckList,
  Spacer,
  Spinner,
  Switch,
  Tabs,
  Text,
  TextArrayInput,
  Title3 as Title,
  Toaster,
  Tooltip,
  atom,
  confirm,
  showModal,
  theme,
  useAtom,
  useCopy,
  useDialog,
  useDialogContext,
  useIsGroup,
  useIsInDialog
};
//# sourceMappingURL=index.js.map